import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Menu, Terminal, FileCode, RotateCcw } from 'lucide-react';
import { translations as defaultTranslations } from '@/LanguageContext';
import { DEFAULT_DETAIL } from '@/pages/ProjectDetailPage';

// ─── API ─────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiFetch = async (path, options = {}, token = null) => {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Bir hata oluştu');
  }
  // Yazma işlemi başarılı → canlı önizleme panelini haberdar et
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET') {
    window.dispatchEvent(new CustomEvent('knx-admin-saved'));
  }
  return res.json();
};

// ─── Auth ─────────────────────────────────────────────────────────
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const login = async (username, password) => {
    const data = await apiFetch('/admin/login', { method: 'POST', body: JSON.stringify({ username, password }) });
    localStorage.setItem('admin_token', data.access_token);
    setToken(data.access_token);
  };
  const logout = () => { localStorage.removeItem('admin_token'); setToken(null); };
  return <AuthContext.Provider value={{ token, login, logout, isAuth: !!token }}>{children}</AuthContext.Provider>;
};

// ─── Ortak UI — DIŞARIDA tanımlandı (input'tan atma sorunu çözümü) ─
const FieldLabel = ({ text }) => (
  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{text}</label>
);

const TextInput = React.memo(({ label, value, onChange, type = 'text', placeholder = '' }) => {
  const [local, setLocal] = React.useState(value ?? '');
  // Parent value değişirse (başka yerden güncelleme) lokal state senkronize et
  const prevRef = React.useRef(value);
  if (prevRef.current !== value && document.activeElement?.dataset?.tinput !== 'true') {
    prevRef.current = value;
    // eslint-disable-next-line react-hooks/rules-of-hooks
  }
  React.useEffect(() => {
    setLocal(value ?? '');
    prevRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = () => {
    if (local !== (value ?? '')) onChange(local);
  };

  return (
    <div>
      {label && <FieldLabel text={label} />}
      <input
        type={type}
        value={local}
        data-tinput="true"
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { commit(); e.target.blur(); } }}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 font-mono text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] hover:border-white/20 transition-all duration-200"
      />
    </div>
  );
});

const TextArea = React.memo(({ label, value, onChange, rows = 4, placeholder = '' }) => {
  const [local, setLocal] = React.useState(value ?? '');
  React.useEffect(() => { setLocal(value ?? ''); }, [value]);
  const commit = () => { if (local !== (value ?? '')) onChange(local); };
  return (
    <div>
      {label && <FieldLabel text={label} />}
      <textarea
        value={local}
        rows={rows}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-md font-mono text-xs text-white/80 px-3 py-2.5 mt-1 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] hover:border-white/20 transition-all duration-200 resize-y"
      />
    </div>
  );
});

// Katlanabilir panel bölümü — başlığa tıklayınca açılır/kapanır
const PanelSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.07] bg-white/[0.015] rounded-lg transition-colors hover:border-white/10">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 group"
      >
        <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 group-hover:text-white/60 transition-colors text-left">{title}</h3>
        <motion.span animate={{ rotate: open ? 0 : -90 }} transition={{ duration: 0.2 }}
          className="text-white/25 group-hover:text-white/50 text-[10px] transition-colors">▼</motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-6 pb-6 pt-1 space-y-4 border-t border-white/5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Btn = ({ children, onClick, variant = 'primary', disabled = false, small = false, title }) => {
  const base = `inline-flex items-center justify-center gap-1.5 font-mono uppercase tracking-[0.15em] rounded-md transition-all duration-200 active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${small ? 'text-[10px] px-3 py-1.5' : 'text-xs px-4 py-2.5'}`;
  const styles = {
    primary: 'bg-white text-black hover:bg-white/90 shadow-[0_0_16px_rgba(255,255,255,0.15)] hover:shadow-[0_0_24px_rgba(255,255,255,0.25)] font-bold',
    secondary: 'border border-white/25 text-white/80 hover:border-white/60 hover:text-white hover:bg-white/[0.07]',
    danger: 'border border-red-400/40 text-red-400 hover:border-red-400/70 hover:bg-red-400/10 hover:shadow-[0_0_14px_rgba(248,113,113,0.2)]',
    ghost: 'text-white/40 hover:text-white hover:bg-white/5',
  };
  return <button onClick={onClick} disabled={disabled} title={title} className={`${base} ${styles[variant]}`}>{children}</button>;
};

// ─── Renk girişi — seçici + hex + parlayan örnek tek satırda ───────
const ColorInput = ({ label, value, onChange, fallback = '#ffffff' }) => {
  const v = value ?? fallback;
  return (
    <div>
      {label && <FieldLabel text={label} />}
      <div className="flex items-center gap-3">
        <input type="color" value={v} onChange={e => onChange(e.target.value)}
          className="w-10 h-10 rounded-md bg-transparent border border-white/15 cursor-pointer hover:border-white/40 transition-colors" />
        <span className="font-mono text-sm text-white/60 w-20">{v}</span>
        <div className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: v, boxShadow: `0 0 10px ${v}` }} />
      </div>
    </div>
  );
};

// ─── Medya kitaplığı seçici modalı ─────────────────────────────────
const MediaPickerModal = ({ onSelect, onClose, filter = 'all' }) => {
  const { token } = useAuth();
  const [media, setMedia] = useState(null);
  useEffect(() => {
    apiFetch('/admin/media', {}, token).then(setMedia).catch(() => setMedia({ images: [], videos: [] }));
  }, [token]);
  const files = media
    ? [
        ...(filter !== 'video' ? media.images.map(p => ({ path: p, type: 'image' })) : []),
        ...(filter !== 'image' ? media.videos.map(p => ({ path: p, type: 'video' })) : []),
      ]
    : null;
  return (
    <motion.div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        className="w-full max-w-3xl max-h-[80vh] overflow-y-auto bg-[#0a0a0a] border border-white/10 rounded-xl p-6"
        initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-white/60">Medya Kitaplığı</h3>
          <Btn onClick={onClose} variant="ghost" small>✕ Kapat</Btn>
        </div>
        {!files && <p className="font-mono text-xs text-white/30 py-10 text-center">Yükleniyor...</p>}
        {files && files.length === 0 && <p className="font-mono text-xs text-white/30 py-10 text-center">Medya bulunamadı — önce Medya sekmesinden yükle.</p>}
        {files && (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {files.map(({ path, type }) => (
              <button key={path} onClick={() => { onSelect(path); onClose(); }}
                className="group relative aspect-square border border-white/10 hover:border-white/60 rounded-md overflow-hidden bg-white/5 transition-all hover:scale-[1.03]">
                {type === 'image'
                  ? <img src={path} alt="" className="w-full h-full object-cover" loading="lazy" />
                  : <video src={path} className="w-full h-full object-cover" muted playsInline preload="metadata" />}
                <span className="absolute bottom-1 right-1 font-mono text-[8px] px-1 py-0.5 rounded bg-black/70 text-white/60 uppercase">{type === 'video' ? 'vid' : 'img'}</span>
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ─── Medya girişi — URL + canlı önizleme + yükle + kitaplıktan seç ─
const MediaInput = ({ label, value, onChange, filter = 'all', placeholder = '/media/images/foto.jpg' }) => {
  const { token } = useAuth();
  const [picker, setPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isVideo = /\.(mp4|mov|webm|ogg)(\?|$)/i.test(value || '');

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/admin/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Yükleme başarısız');
      const data = await res.json();
      onChange(data.path);
    } catch (err) { alert(err.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div>
      {label && <FieldLabel text={label} />}
      <div className="flex gap-2 items-start">
        {/* Önizleme kutusu */}
        <div className="w-16 h-16 flex-shrink-0 rounded-md border border-white/10 overflow-hidden bg-white/5 flex items-center justify-center">
          {value ? (
            isVideo
              ? <video src={value} className="w-full h-full object-cover" muted autoPlay loop playsInline />
              : <img src={value} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
          ) : <span className="font-mono text-[8px] text-white/20 uppercase">boş</span>}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <TextInput value={value ?? ''} onChange={onChange} placeholder={placeholder} />
          <div className="flex gap-1.5">
            <input ref={fileRef} type="file" accept={filter === 'video' ? 'video/*' : filter === 'image' ? 'image/*' : 'image/*,video/*'} onChange={upload} className="hidden" />
            <Btn onClick={() => fileRef.current?.click()} variant="secondary" small disabled={uploading}>{uploading ? '⏳ Yükleniyor...' : '⬆ Yükle'}</Btn>
            <Btn onClick={() => setPicker(true)} variant="secondary" small>🗂 Kitaplık</Btn>
            {value && <Btn onClick={() => onChange('')} variant="ghost" small title="Temizle">✕</Btn>}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {picker && <MediaPickerModal filter={filter} onSelect={onChange} onClose={() => setPicker(false)} />}
      </AnimatePresence>
    </div>
  );
};

// ─── Sürükle-bırak sıralanabilir liste ─────────────────────────────
// items: dizi | onReorder(yeniDizi) | renderItem(item, index) → JSX
const DraggableList = ({ items, onReorder, renderItem, className = 'space-y-2' }) => {
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDrop = (dropIdx) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from === null || from === dropIdx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    onReorder(next);
  };

  return (
    <div className={className}>
      {items.map((item, i) => (
        <div
          key={item.id ?? i}
          draggable
          onDragStart={(e) => { dragIndex.current = i; e.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIndex !== i) setOverIndex(i); }}
          onDragLeave={() => setOverIndex(o => (o === i ? null : o))}
          onDrop={(e) => { e.preventDefault(); handleDrop(i); }}
          onDragEnd={() => { dragIndex.current = null; setOverIndex(null); }}
          className={`relative transition-all duration-150 ${overIndex === i ? 'ring-1 ring-white/40 rounded-md scale-[1.01]' : ''}`}
        >
          <div className="flex items-stretch gap-0">
            {/* Sürükleme tutamacı */}
            <div className="flex items-center px-1.5 cursor-grab active:cursor-grabbing text-white/20 hover:text-white/60 transition-colors select-none"
              title="Sürükleyip bırakarak sırala">
              <span className="text-sm leading-none">⠿</span>
            </div>
            <div className="flex-1 min-w-0">{renderItem(item, i)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Toast = ({ message, type = 'success', onClose }) => (
  <motion.div
    className={`fixed bottom-6 right-6 z-50 px-4 py-3 font-mono text-xs rounded-lg border backdrop-blur-md shadow-lg shadow-black/50 ${type === 'success' ? 'border-green-400/30 text-green-400 bg-green-400/[0.06]' : 'border-red-400/30 text-red-400 bg-red-400/[0.06]'
      }`}
    initial={{ opacity: 0, y: 10, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.96 }}
    onAnimationComplete={() => setTimeout(onClose, 2000)}
  >
    {message}
  </motion.div>
);

// ─── Küçük kontrol bileşenleri (toggle / slider / segment) ─────────
const Toggle = ({ label, value, onChange }) => (
  <button type="button" onClick={() => onChange(!value)} className="flex items-center justify-between w-full gap-3">
    <span className="font-mono text-[11px] text-white/60 text-left">{label}</span>
    <span className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${value ? 'bg-white/80' : 'bg-white/10'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform duration-200 ${value ? 'translate-x-5' : ''}`} />
    </span>
  </button>
);

const Slider = ({ label, value, min, max, step = 1, unit = '', onChange }) => (
  <div>
    {label && <FieldLabel text={label} />}
    <div className="flex items-center gap-4">
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))} className="flex-1 accent-white" />
      <span className="font-mono text-sm text-white/50 w-16 text-right">{value}{unit}</span>
    </div>
  </div>
);

const SegControl = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(o => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)}
        className={`font-mono text-[11px] px-4 py-2 rounded-md border transition-all duration-200 ${value === o.value ? 'border-white text-white bg-white/10' : 'border-white/10 text-white/40 hover:border-white/30'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

// ─── Login ────────────────────────────────────────────────────────
const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!username || !password) return;
    setLoading(true); setError('');
    try { await login(username, password); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" style={{ cursor: 'default' }}>
      <motion.div className="w-full max-w-sm" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="mb-10 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 mb-3">admin panel</p>
          <h1 className="text-2xl font-bold tracking-tight">KNOXOPIA</h1>
        </div>
        <div className="space-y-4">
          <div>
            <FieldLabel text="Kullanıcı Adı" />
            <input type="text" value={username} onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 font-mono text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] transition-all duration-200"
              placeholder="admin" />
          </div>
          <div>
            <FieldLabel text="Şifre" />
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 font-mono text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] transition-all duration-200"
              placeholder="••••••••" />
          </div>
          {error && <p className="font-mono text-xs text-red-400 border border-red-400/20 px-3 py-2">{error}</p>}
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-3 bg-white text-black font-mono text-xs uppercase tracking-[0.2em] rounded-md hover:bg-white/90 active:scale-[0.99] disabled:opacity-50 transition-all duration-200">
            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Projeler ─────────────────────────────────────────────────────
const ProjectsTab = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sSaving, setSSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [form, setForm] = useState({ title: '', author: '', image: '', tags: '', color: '#ffffff', order: 0, description: '', images: '', video_url: '', client: '', year: '', link: '' });

  const load = useCallback(async () => {
    try { const data = await apiFetch('/admin/projects', {}, token); setProjects(data); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  }, [token]);

  useEffect(() => {
    load();
    apiFetch('/admin/settings', {}, token).then(setSettings).catch(() => { });
  }, [load, token]);

  const openNew = () => { setForm({ title: '', author: '', image: '', tags: '', color: '#ffffff', order: 0, description: '', images: '', video_url: '', client: '', year: '', link: '' }); setEditing('new'); };
  const openEdit = (p) => { setForm({ ...p, tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''), images: Array.isArray(p.images) ? p.images.join('\n') : (p.images || '') }); setEditing(p); };
  const f = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));
  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean), images: form.images ? form.images.split('\n').map(s => s.trim()).filter(Boolean) : [] };
      if (editing === 'new') await apiFetch('/admin/projects', { method: 'POST', body: JSON.stringify(payload) }, token);
      else await apiFetch(`/admin/projects/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) }, token);
      setToast({ message: 'Kaydedildi', type: 'success' });
      setEditing(null); load();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Silmek istediğine emin misin?')) return;
    try { await apiFetch(`/admin/projects/${id}`, { method: 'DELETE' }, token); setToast({ message: 'Silindi', type: 'success' }); load(); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const saveSettings = async () => {
    setSSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'Görünüm ayarları kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSSaving(false); }
  };

  // Sürükle-bırak sıralama — anında kaydedilir
  const reorder = async (next) => {
    setProjects(next);
    try {
      await apiFetch('/admin/projects-reorder', {
        method: 'PUT',
        body: JSON.stringify(next.map((p, i) => ({ id: p.id, order: i }))),
      }, token);
      setToast({ message: 'Sıralama kaydedildi', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); load(); }
  };

  // Neon ok renkleri state (string olarak tutulur, virgülle ayrılır)
  const arrowColorsStr = settings?.projects_arrow_colors?.join(', ') ?? '#00f0ff, #bc13fe, #ff003c, #ffed00, #00ff88';
  const setArrowColors = (val) => s('projects_arrow_colors')(val.split(',').map(c => c.trim()).filter(Boolean));

  return (
    <div className="space-y-8">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      {/* ─── Proje listesi ─────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Projeler <span className="text-white/20">({projects.length})</span></h2>
          <Btn onClick={openNew}>+ Yeni Proje</Btn>
        </div>

        <AnimatePresence>
          {editing && (
            <motion.div className="mb-6 border border-white/10 p-6 space-y-4"
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{editing === 'new' ? 'Yeni Proje' : 'Düzenle'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Başlık" value={form.title} onChange={f('title')} placeholder="NVIDIA®" />
                <TextInput label="Yazar" value={form.author} onChange={f('author')} placeholder="Creative Team" />
                <MediaInput label="Kapak görseli" value={form.image} onChange={f('image')} filter="image" placeholder="/media/images/proje.jpg" />
                <TextInput label="Etiketler (virgülle)" value={form.tags} onChange={f('tags')} placeholder="UI, Web Design" />
                <div>
                  <FieldLabel text="Hover Rengi (neon glow rengi)" />
                  <div className="flex items-center gap-3 mt-1">
                    <input type="color" value={form.color} onChange={e => f('color')(e.target.value)}
                      className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
                    <span className="font-mono text-sm text-white/50">{form.color}</span>
                    <div className="w-6 h-6 rounded-full" style={{ backgroundColor: form.color, boxShadow: `0 0 10px ${form.color}` }} />
                  </div>
                </div>
                <TextInput label="Sıra" value={String(form.order)} onChange={v => f('order')(Number(v))} type="number" placeholder="0" />
                <TextInput label="Müşteri" value={form.client ?? ''} onChange={f('client')} placeholder="Nike Labs" />
                <TextInput label="Yıl" value={form.year ?? ''} onChange={f('year')} placeholder="2024" />
                <TextInput label="Proje Linki" value={form.link ?? ''} onChange={f('link')} placeholder="https://..." />
                <MediaInput label="Video" value={form.video_url ?? ''} onChange={f('video_url')} filter="video" placeholder="https://... .mp4" />
              </div>
              <div className="space-y-3">
                <div>
                  <FieldLabel text="Açıklama" />
                  <textarea
                    value={form.description ?? ''}
                    onChange={e => f('description')(e.target.value)}
                    placeholder="Proje hakkında detaylı açıklama..."
                    rows={4}
                    className="w-full bg-transparent border border-white/10 px-3 py-2 font-mono text-sm text-white/80 focus:outline-none focus:border-white/30 resize-none mt-1"
                  />
                </div>
                <div>
                  <FieldLabel text="Ek Görseller (her satıra bir URL)" />
                  <textarea
                    value={form.images ?? ''}
                    onChange={e => f('images')(e.target.value)}
                    placeholder="/media/images/img1.jpg&#10;/media/images/img2.jpg"
                    rows={3}
                    className="w-full bg-transparent border border-white/10 px-3 py-2 font-mono text-sm text-white/80 focus:outline-none focus:border-white/30 resize-none mt-1"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Btn onClick={save} disabled={loading}>{loading ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
                <Btn onClick={() => setEditing(null)} variant="secondary">İptal</Btn>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="font-mono text-[9px] text-white/25 mb-2 uppercase tracking-widest">⠿ tutamacından sürükleyip bırakarak sırala — otomatik kaydedilir</p>
        <DraggableList
          items={projects}
          onReorder={reorder}
          renderItem={(p) => (
            <div className="flex items-center gap-4 border border-white/5 rounded-md p-4 hover:border-white/15 hover:bg-white/[0.02] transition-all">
              <div className="w-12 h-12 flex-shrink-0 rounded bg-cover bg-center border border-white/10" style={{ backgroundImage: `url(${p.image})` }} />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm font-bold truncate">{p.title}</p>
                <p className="font-mono text-xs text-white/30">{p.author}</p>
                <div className="flex gap-1 mt-1">
                  {p.tags.map(t => <span key={t} className="font-mono text-[9px] px-1.5 py-0.5 border border-white/10 text-white/40">{t}</span>)}
                </div>
              </div>
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: p.color, boxShadow: `0 0 8px ${p.color}` }} />
              <div className="flex gap-2 flex-shrink-0">
                <Btn onClick={() => openEdit(p)} variant="secondary" small>✎ Düzenle</Btn>
                <Btn onClick={() => del(p.id)} variant="danger" small>🗑 Sil</Btn>
              </div>
            </div>
          )}
        />
        {projects.length === 0 && <p className="font-mono text-xs text-white/20 py-8 text-center">Henüz proje yok.</p>}
      </div>

      {/* ─── Görünüm Ayarları ─────────────────────── */}
      {settings && (
        <div className="border border-white/5">
          <button
            onClick={() => setSettingsOpen(prev => !prev)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/3 transition-colors"
          >
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Görünüm Ayarları</h3>
            <motion.span
              animate={{ rotate: settingsOpen ? 180 : 0 }}
              transition={{ duration: 0.25 }}
              className="font-mono text-white/30 text-xs"
            >▼</motion.span>
          </button>
          <AnimatePresence initial={false}>
            {settingsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                style={{ overflow: 'hidden' }}
              >
                <div className="px-6 pb-6 space-y-6">
                  <div className="flex justify-end pt-2">
                    <Btn onClick={saveSettings} disabled={sSaving}>{sSaving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
                  </div>

                  {/* Başlık yazıları */}
                  <PanelSection title="📝 Başlık Yazıları">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <FieldLabel text="Satır 1" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">Su dolma efektli büyük başlık — 1. satır</span>
                        <TextInput value={settings.projects_line1 ?? 'SEE THE WORK'} onChange={s('projects_line1')} placeholder="SEE THE WORK" />
                      </div>
                      <div>
                        <FieldLabel text="Satır 2" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">Su dolma efektli büyük başlık — 2. satır</span>
                        <TextInput value={settings.projects_line2 ?? 'OF OUR STUDENTS'} onChange={s('projects_line2')} placeholder="OF OUR STUDENTS" />
                      </div>
                    </div>
                  </PanelSection>

                  {/* Su dolma efekti */}
                  <PanelSection title="💧 Su Dolma Efekti (Başlık Animasyonu)">
                    <p className="font-mono text-[10px] text-white/30 mb-4">Başlık scroll ile görünüme girince aşağıdan yukarı dolan renk efekti.</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <FieldLabel text="Dolma rengi" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">Yazının dolduğu renk</span>
                        <div className="flex items-center gap-3 mt-1">
                          <input type="color" value={settings.projects_fill_color ?? '#ffffff'} onChange={e => s('projects_fill_color')(e.target.value)}
                            className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
                          <span className="font-mono text-sm text-white/50">{settings.projects_fill_color ?? '#ffffff'}</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Kontur rengi" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">Dolmadan önceki yazı rengi</span>
                        <TextInput value={settings.projects_outline_color ?? 'rgba(255,255,255,0.15)'} onChange={s('projects_outline_color')} placeholder="rgba(255,255,255,0.15)" />
                      </div>
                      <div>
                        <FieldLabel text="Dolma süresi (sn)" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">1 = hızlı &nbsp;|&nbsp; 3 = yavaş</span>
                        <div className="flex items-center gap-4">
                          <input type="range" min="0.5" max="4" step="0.1"
                            value={settings.projects_fill_duration ?? 1.8}
                            onChange={e => s('projects_fill_duration')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-10">{settings.projects_fill_duration ?? 1.8}s</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Başlama gecikmesi (sn)" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">Görünüme girince dolmaya başlamadan önceki süre</span>
                        <div className="flex items-center gap-4">
                          <input type="range" min="0" max="2" step="0.1"
                            value={settings.projects_fill_delay ?? 0.3}
                            onChange={e => s('projects_fill_delay')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-10">{settings.projects_fill_delay ?? 0.3}s</span>
                        </div>
                      </div>
                    </div>
                  </PanelSection>

                  {/* Layout */}
                  <PanelSection title="📐 Kart Düzeni">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <FieldLabel text="Aynı anda görünen kart sayısı" />
                        <span className="font-mono text-[9px] text-white/20 block mb-2">2 = büyük &nbsp;|&nbsp; 3 = dengeli &nbsp;|&nbsp; 4 = küçük</span>
                        <div className="flex gap-2">
                          {[2, 3, 4].map(n => (
                            <button key={n} onClick={() => s('projects_visible')(n)}
                              className={`font-mono text-xs px-4 py-2 border transition-colors ${(settings.projects_visible ?? 3) === n
                                ? 'border-white text-white bg-white/10'
                                : 'border-white/10 text-white/40 hover:border-white/30'
                                }`}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Kartlar arası boşluk" />
                        <div className="flex gap-2 mt-2">
                          {[
                            { label: 'Dar', value: 'gap-2' },
                            { label: 'Normal', value: 'gap-6' },
                            { label: 'Geniş', value: 'gap-10' },
                          ].map(opt => (
                            <button key={opt.value} onClick={() => s('projects_card_gap')(opt.value)}
                              className={`font-mono text-[10px] px-3 py-2 border transition-colors ${(settings.projects_card_gap ?? 'gap-6') === opt.value
                                ? 'border-white text-white bg-white/10'
                                : 'border-white/10 text-white/40 hover:border-white/30'
                                }`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Kart görsel oranı" />
                        <div className="flex flex-wrap gap-2 mt-2">
                          {[
                            { label: 'Yatay 4/3', value: 'aspect-[4/3]' },
                            { label: 'Kare 1/1', value: 'aspect-[1/1]' },
                            { label: 'Dikey 3/4', value: 'aspect-[3/4]' },
                            { label: 'Dikey 2/3', value: 'aspect-[2/3]' },
                          ].map(opt => (
                            <button key={opt.value} onClick={() => s('projects_card_aspect')(opt.value)}
                              className={`font-mono text-[10px] px-3 py-2 border transition-colors ${(settings.projects_card_aspect ?? 'aspect-[3/4]') === opt.value
                                ? 'border-white text-white bg-white/10'
                                : 'border-white/10 text-white/40 hover:border-white/30'
                                }`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PanelSection>

                  {/* Neon oklar */}
                  <PanelSection title="⬅️➡️ Neon Ok Efekti">
                    <p className="font-mono text-[10px] text-white/30 mb-4">Slider oklarının hover'da aldığı neon renk efekti.</p>
                    <div className="space-y-4">
                      <div>
                        <FieldLabel text="Neon renk paleti" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">Hover'da bu renklerden rastgele seçilir. Virgülle ayır.</span>
                        <TextInput value={arrowColorsStr} onChange={setArrowColors} placeholder="#00f0ff, #bc13fe, #ff003c" />
                        <div className="flex gap-2 mt-2">
                          {(settings.projects_arrow_colors ?? ['#00f0ff', '#bc13fe', '#ff003c', '#ffed00', '#00ff88']).map((c, i) => (
                            <div key={i} className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: c, boxShadow: `0 0 8px ${c}` }} />
                          ))}
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Glow kuvveti" />
                        <span className="font-mono text-[9px] text-white/20 block mb-2">1 = sade &nbsp;|&nbsp; 3 = güçlü bloom</span>
                        <div className="flex gap-2">
                          {[1, 2, 3].map(n => (
                            <button key={n} onClick={() => s('projects_arrow_glow')(n)}
                              className={`font-mono text-xs px-4 py-2 border transition-colors ${(settings.projects_arrow_glow ?? 3) === n
                                ? 'border-white text-white bg-white/10'
                                : 'border-white/10 text-white/40 hover:border-white/30'
                                }`}>
                              {n === 1 ? '1 — Sade' : n === 2 ? '2 — Orta' : '3 — Güçlü'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PanelSection>

                  {/* Kart hover efekti */}
                  <PanelSection title="🌈 Kart Hover Efekti">
                    <p className="font-mono text-[10px] text-white/30 mb-4">Proje kartının üzerine gelinince olan renk, zoom ve neon glow efektleri.</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <FieldLabel text="Renk katmanı opaklığı" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">0.1 = hafif &nbsp;|&nbsp; 0.5 = yoğun</span>
                        <div className="flex items-center gap-4">
                          <input type="range" min="0" max="0.7" step="0.05"
                            value={settings.projects_overlay_opacity ?? 0.3}
                            onChange={e => s('projects_overlay_opacity')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-10">{settings.projects_overlay_opacity ?? 0.3}</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Görsel zoom miktarı" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">1.0 = yok &nbsp;|&nbsp; 1.15 = belirgin</span>
                        <div className="flex items-center gap-4">
                          <input type="range" min="1.0" max="1.2" step="0.01"
                            value={settings.projects_image_scale ?? 1.08}
                            onChange={e => s('projects_image_scale')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-14">{settings.projects_image_scale ?? 1.08}×</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Parlaklık artışı" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">1.0 = sabit &nbsp;|&nbsp; 1.3 = çok parlak</span>
                        <div className="flex items-center gap-4">
                          <input type="range" min="1.0" max="1.5" step="0.05"
                            value={settings.projects_image_brightness ?? 1.1}
                            onChange={e => s('projects_image_brightness')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-10">{settings.projects_image_brightness ?? 1.1}</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Neon glow opaklığı" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">0 = yok &nbsp;|&nbsp; 0.4 = belirgin</span>
                        <div className="flex items-center gap-4">
                          <input type="range" min="0" max="0.5" step="0.02"
                            value={settings.projects_glow_opacity ?? 0.12}
                            onChange={e => s('projects_glow_opacity')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-10">{settings.projects_glow_opacity ?? 0.12}</span>
                        </div>
                      </div>
                      <div>
                        <FieldLabel text="Neon glow yayılımı" />
                        <span className="font-mono text-[9px] text-white/20 block mb-1">200px = dar &nbsp;|&nbsp; 500px = geniş</span>
                        <TextInput value={settings.projects_glow_spread ?? '320px'} onChange={s('projects_glow_spread')} placeholder="320px" />
                      </div>
                      <div>
                        <FieldLabel text="Efekt geçiş süresi (sn)" />
                        <div className="flex items-center gap-4">
                          <input type="range" min="0.1" max="1.5" step="0.1"
                            value={settings.projects_glow_duration ?? 0.6}
                            onChange={e => s('projects_glow_duration')(Number(e.target.value))}
                            className="flex-1 accent-white" />
                          <span className="font-mono text-sm text-white/50 w-10">{settings.projects_glow_duration ?? 0.6}s</span>
                        </div>
                      </div>
                    </div>
                  </PanelSection>

                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};


// ─── Hero ─────────────────────────────────────────────────────────
const HeroTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token).then(setSettings).catch(() => { });
  }, [token]);

  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'Kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="font-mono text-xs text-white/30 py-8 text-center">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Hero Bölümü</h2>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
      </div>

      <PanelSection title="Yazılar & Font Boyutları">
        <p className="font-mono text-[10px] text-white/30 mb-3">Her satır için yazıyı düzenle, boyutunu ayarla veya gizle.</p>
        <div className="space-y-3">
          {/* Satır 1 */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 1</span>
              <button onClick={() => s('hero_line1_visible')(!(settings.hero_line1_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.hero_line1_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.hero_line1_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <TextInput label="Yazı" value={settings.hero_line1 ?? 'CREATE'} onChange={s('hero_line1')} placeholder="CREATE" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="4" max="25" step="0.5" value={settings.hero_line1_size_mobile ?? 14} onChange={e => s('hero_line1_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.hero_line1_size_mobile ?? 14}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="15" step="0.5" value={settings.hero_line1_size_desktop ?? 5} onChange={e => s('hero_line1_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.hero_line1_size_desktop ?? 5}rem</span>
                </div>
              </div>
            </div>
          </div>
          {/* Satır 2 — Neon */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 2 <span className="text-white/20">(Neon SVG)</span></span>
              <button onClick={() => s('hero_line2_visible')(!(settings.hero_line2_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.hero_line2_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.hero_line2_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <TextInput label="Yazı" value={settings.hero_line2 ?? 'DIGITAL'} onChange={s('hero_line2')} placeholder="DIGITAL" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="4" max="25" step="0.5" value={settings.hero_line2_size_mobile ?? 11} onChange={e => s('hero_line2_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.hero_line2_size_mobile ?? 11}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="15" step="0.5" value={settings.hero_line2_size_desktop ?? 8} onChange={e => s('hero_line2_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.hero_line2_size_desktop ?? 8}rem</span>
                </div>
              </div>
            </div>
          </div>
          {/* Satır 3 */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 3</span>
              <button onClick={() => s('hero_line3_visible')(!(settings.hero_line3_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.hero_line3_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.hero_line3_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <TextInput label="Yazı" value={settings.hero_line3 ?? 'EXPERIENCES'} onChange={s('hero_line3')} placeholder="EXPERIENCES" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="4" max="25" step="0.5" value={settings.hero_line3_size_mobile ?? 9} onChange={e => s('hero_line3_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.hero_line3_size_mobile ?? 9}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="15" step="0.5" value={settings.hero_line3_size_desktop ?? 6.5} onChange={e => s('hero_line3_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.hero_line3_size_desktop ?? 6.5}rem</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <MediaInput label="Video yolu" value={settings.hero_video_src ?? '/media/videos/test.mp4'} onChange={s('hero_video_src')} filter="video" placeholder="/media/videos/hero.mp4" />
      </PanelSection>

      <PanelSection title="Arkaplan & Overlay">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <FieldLabel text="Solid Overlay Opaklığı" />
            <span className="font-mono text-[9px] text-white/20 block mb-1">0 = şeffaf &nbsp;|&nbsp; 100 = tam siyah</span>
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="100" step="5"
                value={settings.hero_overlay_opacity ?? 70}
                onChange={e => s('hero_overlay_opacity')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-10 text-right">{settings.hero_overlay_opacity ?? 70}%</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Gradient Yönü" />
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { label: 'Alt → Üst', value: 'to-top' },
                { label: 'Üst → Alt', value: 'to-bottom' },
                { label: 'Sol → Sağ', value: 'to-right' },
                { label: 'Tüm Kenarlar', value: 'radial' },
              ].map(opt => (
                <button key={opt.value} onClick={() => s('hero_gradient_dir')(opt.value)}
                  className={`font-mono text-[10px] px-3 py-2 border transition-colors ${(settings.hero_gradient_dir ?? 'to-top') === opt.value
                    ? 'border-white text-white bg-white/10'
                    : 'border-white/10 text-white/40 hover:border-white/30 hover:text-white/70'
                    }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <FieldLabel text="Gradient Başlangıç Opaklığı" />
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="100" step="5"
                value={settings.hero_gradient_from ?? 80}
                onChange={e => s('hero_gradient_from')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-10 text-right">{settings.hero_gradient_from ?? 80}%</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Gradient Bitiş Opaklığı" />
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="100" step="5"
                value={settings.hero_gradient_to ?? 0}
                onChange={e => s('hero_gradient_to')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-10 text-right">{settings.hero_gradient_to ?? 0}%</span>
            </div>
          </div>
        </div>
      </PanelSection>

      <PanelSection title="Neon Efekt (Satır 2)">
        <div className="space-y-3">
          <FieldLabel text="Renk Paleti" />
          <div className="flex flex-wrap gap-3">
            {(settings.neon_colors ?? ['#f7640f', '#bc13fe', '#fbff2d', '#0ff796']).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="color" value={c}
                  onChange={e => { const nc = [...(settings.neon_colors ?? [])]; nc[i] = e.target.value; setSettings(prev => ({ ...prev, neon_colors: nc })); }}
                  className="w-8 h-8 bg-transparent border border-white/10 cursor-pointer" />
                <span className="font-mono text-xs text-white/40">{c}</span>
                {(settings.neon_colors ?? []).length > 2 && (
                  <button onClick={() => setSettings(prev => ({ ...prev, neon_colors: prev.neon_colors.filter((_, idx) => idx !== i) }))}
                    className="font-mono text-[10px] text-red-400/50 hover:text-red-400">×</button>
                )}
              </div>
            ))}
            <button onClick={() => setSettings(prev => ({ ...prev, neon_colors: [...(prev.neon_colors ?? []), '#ffffff'] }))}
              className="font-mono text-[10px] text-white/20 hover:text-white/60 border border-white/10 px-2 py-1 transition-colors">+ Renk Ekle</button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <TextInput label="Çizgi kalınlığı (px)" value={String(settings.neon_stroke_width ?? 1.5)} onChange={v => s('neon_stroke_width')(Number(v))} type="number" />
          <TextInput label="Glow boyutu" value={settings.neon_glow_size ?? '8px'} onChange={s('neon_glow_size')} placeholder="8px" />
          <TextInput label="Hız min (sn)" value={String(settings.neon_speed_min ?? 5)} onChange={v => s('neon_speed_min')(Number(v))} type="number" />
          <TextInput label="Hız max (sn)" value={String(settings.neon_speed_max ?? 35)} onChange={v => s('neon_speed_max')(Number(v))} type="number" />
        </div>
      </PanelSection>
    </div>
  );
};

// ─── Philosophy ───────────────────────────────────────────────────
const PhilosophyTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token).then(setSettings).catch(() => { });
  }, [token]);

  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'Kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="font-mono text-xs text-white/30 py-8 text-center">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Philosophy Bölümü</h2>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
      </div>

      <div className="bg-white/5 border border-white/10 p-4 font-mono text-[10px] text-white/50 uppercase tracking-widest">
        💡 Not: Başlıklar, satırlar ve paragraflar artık <b>"ÇELİRİLER (Translations)"</b> sekmesinden dile göre (Türkçe/İngilizce) yönetilmektedir.
      </div>

      <PanelSection title="Font Boyutları & Görünürlük">
        <p className="font-mono text-[10px] text-white/30 mb-3">Satırların boyutunu ayarla veya tamamen gizle.</p>
        <div className="space-y-3">
          {/* Satır 1 */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 1</span>
              <button onClick={() => s('philosophy_line1_visible')(!(settings.philosophy_line1_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.philosophy_line1_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.philosophy_line1_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="3" max="18" step="0.5" value={settings.philosophy_line1_size_mobile ?? 7.5} onChange={e => s('philosophy_line1_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line1_size_mobile ?? 7.5}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="10" step="0.25" value={settings.philosophy_line1_size_desktop ?? 4.5} onChange={e => s('philosophy_line1_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line1_size_desktop ?? 4.5}rem</span>
                </div>
              </div>
            </div>
          </div>
          {/* Satır 2 — Neon */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 2 <span className="text-white/20">(Neon SVG)</span></span>
              <button onClick={() => s('philosophy_line2_visible')(!(settings.philosophy_line2_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.philosophy_line2_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.philosophy_line2_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="3" max="18" step="0.5" value={settings.philosophy_line2_size_mobile ?? 7.5} onChange={e => s('philosophy_line2_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line2_size_mobile ?? 7.5}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="10" step="0.25" value={settings.philosophy_line2_size_desktop ?? 4.5} onChange={e => s('philosophy_line2_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line2_size_desktop ?? 4.5}rem</span>
                </div>
              </div>
            </div>
          </div>
          {/* Satır 3 */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 3</span>
              <button onClick={() => s('philosophy_line3_visible')(!(settings.philosophy_line3_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.philosophy_line3_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.philosophy_line3_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="3" max="18" step="0.5" value={settings.philosophy_line3_size_mobile ?? 7.5} onChange={e => s('philosophy_line3_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line3_size_mobile ?? 7.5}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="10" step="0.25" value={settings.philosophy_line3_size_desktop ?? 4.5} onChange={e => s('philosophy_line3_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line3_size_desktop ?? 4.5}rem</span>
                </div>
              </div>
            </div>
          </div>
          {/* Satır 4 */}
          <div className="border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">Satır 4</span>
              <button onClick={() => s('philosophy_line4_visible')(!(settings.philosophy_line4_visible ?? true))}
                className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.philosophy_line4_visible ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'}`}>
                {(settings.philosophy_line4_visible ?? true) ? '● Görünür' : '○ Gizli'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Mobil boyut (vw)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="3" max="18" step="0.5" value={settings.philosophy_line4_size_mobile ?? 7.5} onChange={e => s('philosophy_line4_size_mobile')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line4_size_mobile ?? 7.5}vw</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Masaüstü boyut (rem)" />
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="10" step="0.25" value={settings.philosophy_line4_size_desktop ?? 4.5} onChange={e => s('philosophy_line4_size_desktop')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-sm text-white/50 w-14">{settings.philosophy_line4_size_desktop ?? 4.5}rem</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PanelSection>

      <PanelSection title="Görünüm Ayarları">
        <div className="space-y-4">
          <MediaInput label="Fotoğraf yolu" value={settings.philosophy_photo ?? '/media/images/dhual.jpg'} onChange={s('philosophy_photo')} filter="image" placeholder="/media/images/photo.jpg" />
        </div>
      </PanelSection>

      <PanelSection title="Neon Efekt (Satır 2 — CONSULTANTS)">
        <div className="space-y-3">
          <FieldLabel text="Renk Paleti" />
          <div className="flex flex-wrap gap-3">
            {(settings.philosophy_neon_colors ?? ['#00f0ff', '#bc13fe', '#ff003c', '#00f0ff']).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="color" value={c}
                  onChange={e => { const nc = [...(settings.philosophy_neon_colors ?? ['#00f0ff', '#bc13fe', '#ff003c', '#00f0ff'])]; nc[i] = e.target.value; setSettings(prev => ({ ...prev, philosophy_neon_colors: nc })); }}
                  className="w-8 h-8 bg-transparent border border-white/10 cursor-pointer" />
                <span className="font-mono text-xs text-white/40">{c}</span>
                {(settings.philosophy_neon_colors ?? []).length > 2 && (
                  <button onClick={() => setSettings(prev => ({ ...prev, philosophy_neon_colors: (prev.philosophy_neon_colors ?? []).filter((_, idx) => idx !== i) }))}
                    className="font-mono text-[10px] text-red-400/50 hover:text-red-400">×</button>
                )}
              </div>
            ))}
            <button onClick={() => setSettings(prev => ({ ...prev, philosophy_neon_colors: [...(prev.philosophy_neon_colors ?? ['#00f0ff', '#bc13fe', '#ff003c', '#00f0ff']), '#ffffff'] }))}
              className="font-mono text-[10px] text-white/20 hover:text-white/60 border border-white/10 px-2 py-1 transition-colors">+ Renk Ekle</button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <TextInput label="Çizgi kalınlığı" value={String(settings.philosophy_neon_stroke ?? 2)} onChange={v => s('philosophy_neon_stroke')(Number(v))} type="number" />
          <TextInput label="Glow boyutu" value={settings.philosophy_neon_glow ?? '18px'} onChange={s('philosophy_neon_glow')} placeholder="18px" />
          <TextInput label="Hız min (sn)" value={String(settings.philosophy_neon_speed_min ?? 10)} onChange={v => s('philosophy_neon_speed_min')(Number(v))} type="number" />
          <TextInput label="Hız max (sn)" value={String(settings.philosophy_neon_speed_max ?? 22)} onChange={v => s('philosophy_neon_speed_max')(Number(v))} type="number" />
        </div>
      </PanelSection>

      <PanelSection title="Streaming Platform Logoları & IMDb">
        <p className="font-mono text-[10px] text-white/30 mb-3">Medya sekmesinden yüklediğiniz logoların yollarını girin.</p>
        <div className="space-y-4">
          <div className="border border-white/5 p-4 space-y-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 mb-2 block">Logo Yolları</span>
            <TextInput label="Netflix Logo" value={settings.philosophy_netflix_logo ?? ''} onChange={s('philosophy_netflix_logo')} placeholder="/media/images/netflix.png" />
            <TextInput label="Disney+ Logo" value={settings.philosophy_disneyplus_logo ?? ''} onChange={s('philosophy_disneyplus_logo')} placeholder="/media/images/disneyplus.png" />
            <TextInput label="Amazon Prime Logo" value={settings.philosophy_amazonprime_logo ?? ''} onChange={s('philosophy_amazonprime_logo')} placeholder="/media/images/prime.png" />
          </div>
          <div className="border border-white/5 p-4 space-y-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 mb-2 block">IMDb</span>
            <TextInput label="IMDb Linki" value={settings.philosophy_imdb_url ?? 'https://www.imdb.com'} onChange={s('philosophy_imdb_url')} placeholder="https://www.imdb.com/name/..." />
          </div>
        </div>
      </PanelSection>
    </div>
  );
};

// ─── Splash Screen ────────────────────────────────────────────────
const SplashTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token).then(setSettings).catch(() => { });
  }, [token]);

  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'Kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="font-mono text-xs text-white/30 py-8 text-center">Yükleniyor...</p>;

  const SOCIAL_FIELDS = [
    { key: 'social_instagram', label: 'Instagram', placeholder: 'https://instagram.com/knoxopia' },
    { key: 'social_twitter', label: 'X / Twitter', placeholder: 'https://x.com/knoxopia' },
    { key: 'social_linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/knoxopia' },
    { key: 'social_behance', label: 'Behance', placeholder: 'https://behance.net/knoxopia' },
    { key: 'social_youtube', label: 'YouTube', placeholder: 'https://youtube.com/@knoxopia' },
    { key: 'social_telegram', label: 'Telegram', placeholder: 'https://t.me/knoxopia' },
    { key: 'social_tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@knoxopia' },
    { key: 'social_github', label: 'GitHub', placeholder: 'https://github.com/knoxopia' },
    { key: 'social_dribbble', label: 'Dribbble', placeholder: 'https://dribbble.com/knoxopia' },
    { key: 'social_imdb', label: 'IMDb', placeholder: 'https://www.imdb.com/title/...' },
  ];

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Splash Screen</h2>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
      </div>

      {/* Sosyal Medya */}
      <PanelSection title="Sosyal Medya Linkleri">
        <p className="font-mono text-[10px] text-white/30 mb-3">Boş bırakılan platformlar splash screen'de gösterilmez.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
            <TextInput key={key} label={label} value={settings[key] ?? ''} onChange={s(key)} placeholder={placeholder} />
          ))}
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={() => s('splash_show_socials')(!(settings.splash_show_socials ?? true))}
            className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.splash_show_socials ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'
              }`}>
            {(settings.splash_show_socials ?? true) ? '● Sosyal İkonlar Görünür' : '○ Sosyal İkonlar Gizli'}
          </button>
        </div>
      </PanelSection>

      {/* Yazılar */}
      <PanelSection title="Yazılar">
        <div className="grid md:grid-cols-2 gap-4">
          <TextInput label="Üst yazı (Welcome to)" value={settings.splash_welcome_text ?? 'Welcome to'} onChange={s('splash_welcome_text')} placeholder="Welcome to" />
          <TextInput label="Alt buton yazısı" value={settings.splash_cta_text ?? 'KNOXOPIA'} onChange={s('splash_cta_text')} placeholder="KNOXOPIA" />
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <FieldLabel text="Yazı rengi" />
            <div className="flex items-center gap-3 mt-1">
              <input type="color"
                value={settings.splash_text_color?.startsWith('#') ? settings.splash_text_color : '#ffffff'}
                onChange={e => s('splash_text_color')(e.target.value)}
                className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
              <span className="font-mono text-sm text-white/50">{settings.splash_text_color ?? '#ffffff'}</span>
            </div>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => s('splash_text_glow')(!(settings.splash_text_glow ?? true))}
              className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.splash_text_glow ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'
                }`}>
              {(settings.splash_text_glow ?? true) ? '● Yazı Glow Açık' : '○ Yazı Glow Kapalı'}
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <FieldLabel text="Yazı rengi" />
            <div className="flex items-center gap-3 mt-1">
              <input type="color"
                value={settings.splash_text_color?.startsWith('#') ? settings.splash_text_color : '#ffffff'}
                onChange={e => s('splash_text_color')(e.target.value)}
                className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
              <span className="font-mono text-sm text-white/50">{settings.splash_text_color ?? 'rgba(255,255,255,0.9)'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={() => s('splash_text_glow')(!(settings.splash_text_glow ?? true))}
              className={`font-mono text-[9px] px-3 py-1 border transition-colors ${(settings.splash_text_glow ?? true) ? 'border-white/30 text-white/70 bg-white/5' : 'border-white/10 text-white/20'
                }`}>
              {(settings.splash_text_glow ?? true) ? '● Yazı Glow Açık' : '○ Yazı Glow Kapalı'}
            </button>
          </div>
        </div>
      </PanelSection>

      {/* Görünüm */}

      {/* Görünüm */}
      <PanelSection title="Görünüm">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <FieldLabel text="Arka plan rengi" />
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={settings.splash_bg_color ?? '#030303'} onChange={e => s('splash_bg_color')(e.target.value)}
                className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
              <span className="font-mono text-sm text-white/50">{settings.splash_bg_color ?? '#030303'}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Logo ölçeği" />
            <div className="flex items-center gap-4">
              <input type="range" min="0.3" max="1.5" step="0.05"
                value={settings.splash_logo_scale ?? 0.72}
                onChange={e => s('splash_logo_scale')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_logo_scale ?? 0.72}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Kamera uzaklığı" />
            <span className="font-mono text-[9px] text-white/20 block mb-1">Büyük = logo küçük görünür</span>
            <div className="flex items-center gap-4">
              <input type="range" min="3" max="15" step="0.5"
                value={settings.splash_camera_z ?? 7}
                onChange={e => s('splash_camera_z')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_camera_z ?? 7}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Otomatik dönüş hızı" />
            <span className="font-mono text-[9px] text-white/20 block mb-1">0 = durur</span>
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="0.3" step="0.01"
                value={settings.splash_auto_rotate ?? 0.06}
                onChange={e => s('splash_auto_rotate')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_auto_rotate ?? 0.06}</span>
            </div>
          </div>
        </div>
      </PanelSection>

      {/* Işıklandırma */}
      <PanelSection title="3D Işıklandırma">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <FieldLabel text="Chrome metalness" />
            <span className="font-mono text-[9px] text-white/20 block mb-1">0 = mat &nbsp;|&nbsp; 1 = ayna</span>
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="1" step="0.05"
                value={settings.splash_metalness ?? 1.0}
                onChange={e => s('splash_metalness')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_metalness ?? 1.0}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Roughness (pürüzlülük)" />
            <span className="font-mono text-[9px] text-white/20 block mb-1">0 = parlak &nbsp;|&nbsp; 1 = mat</span>
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="1" step="0.01"
                value={settings.splash_roughness ?? 0.03}
                onChange={e => s('splash_roughness')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_roughness ?? 0.03}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Alt rim ışığı rengi" />
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={settings.splash_rim_color ?? '#ff6a00'} onChange={e => s('splash_rim_color')(e.target.value)}
                className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
              <span className="font-mono text-sm text-white/50">{settings.splash_rim_color ?? '#ff6a00'}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Arka aksan ışığı rengi" />
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={settings.splash_accent_color ?? '#0044ff'} onChange={e => s('splash_accent_color')(e.target.value)}
                className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
              <span className="font-mono text-sm text-white/50">{settings.splash_accent_color ?? '#0044ff'}</span>
            </div>
          </div>
        </div>
      </PanelSection>

      {/* Partiküller */}
      <PanelSection title="Partikül Efekti">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <FieldLabel text="Partikül sayısı" />
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="500" step="10"
                value={settings.splash_particle_count ?? 220}
                onChange={e => s('splash_particle_count')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_particle_count ?? 220}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Partikül boyutu" />
            <div className="flex items-center gap-4">
              <input type="range" min="0.005" max="0.1" step="0.005"
                value={settings.splash_particle_size ?? 0.028}
                onChange={e => s('splash_particle_size')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-16">{settings.splash_particle_size ?? 0.028}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Partikül rengi" />
            <div className="flex items-center gap-3 mt-1">
              <input type="color" value={settings.splash_particle_color ?? '#aaccff'} onChange={e => s('splash_particle_color')(e.target.value)}
                className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
              <span className="font-mono text-sm text-white/50">{settings.splash_particle_color ?? '#aaccff'}</span>
            </div>
          </div>
          <div>
            <FieldLabel text="Giriş animasyonu süresi (sn)" />
            <div className="flex items-center gap-4">
              <input type="range" min="0.5" max="5" step="0.1"
                value={settings.splash_intro_duration ?? 2.8}
                onChange={e => s('splash_intro_duration')(Number(e.target.value))}
                className="flex-1 accent-white" />
              <span className="font-mono text-sm text-white/50 w-12">{settings.splash_intro_duration ?? 2.8}s</span>
            </div>
          </div>
        </div>
      </PanelSection>

      <PanelSection title="🌌 Galaktik Arka Plan">
        <p className="font-mono text-[10px] text-white/30 mb-2">Logonun arkasında dumanlı/akışkan nebula: 3 renk katmanı, yıldız parıltıları, fareyle sürüklenme.</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Toggle label="Galaktik arka planı göster" value={settings.splash_bg3d_enabled !== false} onChange={s('splash_bg3d_enabled')} />
          </div>
          <ColorInput label="Renk 1 — derin uzay tabanı" value={settings.splash_galaxy_color1} onChange={s('splash_galaxy_color1')} fallback="#1b2a6e" />
          <ColorInput label="Renk 2 — nebula dumanı" value={settings.splash_galaxy_color2} onChange={s('splash_galaxy_color2')} fallback="#bc13fe" />
          <ColorInput label="Renk 3 — aksan parıltısı" value={settings.splash_galaxy_color3} onChange={s('splash_galaxy_color3')} fallback="#00f0ff" />
          <Slider label="Yoğunluk (parlaklık)" value={settings.splash_galaxy_intensity ?? 0.9} min={0.2} max={2} step={0.1} onChange={s('splash_galaxy_intensity')} />
          <Slider label="Akış hızı" value={settings.splash_galaxy_speed ?? 1} min={0.2} max={3} step={0.1} unit="x" onChange={s('splash_galaxy_speed')} />
        </div>
      </PanelSection>

      <PanelSection title="🎥 Etkileşim & Çıkış Animasyonu">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <Toggle label="Fare ile duman dağılması" value={settings.splash_mouse_disperse !== false} onChange={s('splash_mouse_disperse')} />
            <p className="font-mono text-[9px] text-white/25">İmlecin çevresindeki nebula dumanı dışa doğru dağılır.</p>
          </div>
          <div className="space-y-1">
            <Toggle label="Çıkışta kamera dalışı (solucan deliği)" value={settings.splash_exit_camera !== false} onChange={s('splash_exit_camera')} />
            <p className="font-mono text-[9px] text-white/25">Tıklayınca kamera, logoyla birlikte tünele dalarak siteye girer.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-white/5">
          <Slider label="Dalış hızı" value={settings.splash_exit_speed ?? 0.62} min={0.3} max={1.2} step={0.02}
            onChange={s('splash_exit_speed')} />
          <Slider label="Tünel akış hızı" value={settings.splash_warp_speed ?? 1} min={0.3} max={2.5} step={0.1} unit="x"
            onChange={s('splash_warp_speed')} />
          <Slider label="Tünel parlaklığı" value={settings.splash_warp_brightness ?? 1} min={0.3} max={2} step={0.1} unit="x"
            onChange={s('splash_warp_brightness')} />
          <Slider label="Burgu miktarı" value={settings.splash_warp_twist ?? 0.5} min={0} max={1} step={0.05}
            onChange={s('splash_warp_twist')} />
          <Slider label="Dalış FOV (görüş genişlemesi)" value={settings.splash_warp_fov ?? 80} min={50} max={110} step={2} unit="°"
            onChange={s('splash_warp_fov')} />
        </div>
        <p className="font-mono text-[9px] text-white/25">Düşük dalış hızı = daha uzun, sinematik geçiş. Burgu 0 = düz dalış.</p>
      </PanelSection>
    </div>
  );
};

// ─── Hizmetler (Services) ─────────────────────────────────────────────────────
const ServicesTab = () => {
  const { token } = useAuth();
  const [heading, setHeading] = useState({ label: 'Hizmetler', title: 'Ne Yapıyoruz', subtitle: 'Yaratıcı stüdyomuzun sunduğu hizmetler' });
  const [services, setServices] = useState([
    { id: 'svc1', title: '2D Çalışmalar', subtitle: 'Görsel Tasarım', color: '#f7951d', items: ['Albüm & Şarkı Kapakları', 'Tek Kare Renderlar'] },
    { id: 'svc2', title: '3D Çalışmalar', subtitle: 'Dijital Sanat', color: '#2faee0', items: ['3D Video', 'DJ Visuals'] },
    { id: 'svc3', title: 'Branding', subtitle: 'Kimlik Tasarımı', color: '#bc13fe', items: ['Logo Tasarımı', 'Kurumsal Kimlik'] },
  ]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/services-data', {}, null)
      .then(data => {
        if (data?.heading) setHeading(h => ({ ...h, ...data.heading }));
        if (Array.isArray(data?.services) && data.services.length > 0) setServices(data.services);
      })
      .catch(() => { });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/services-data', { method: 'PUT', body: JSON.stringify({ heading, services }) }, token);
      setToast({ message: 'Kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const addService = () => {
    const id = 'svc' + Date.now();
    setServices(s => [...s, { id, title: 'Yeni Hizmet', subtitle: '', color: '#ffffff', items: ['Madde 1'] }]);
  };

  const removeService = (id) => setServices(s => s.filter(x => x.id !== id));

  const updateService = (id, field, value) =>
    setServices(s => s.map(x => x.id === id ? { ...x, [field]: value } : x));

  const addItem = (id) =>
    setServices(s => s.map(x => x.id === id ? { ...x, items: [...(x.items || []), 'Yeni madde'] } : x));

  const removeItem = (id, i) =>
    setServices(s => s.map(x => x.id === id ? { ...x, items: x.items.filter((_, idx) => idx !== i) } : x));

  const updateItem = (id, i, val) =>
    setServices(s => s.map(x => x.id === id ? { ...x, items: x.items.map((it, idx) => idx === i ? val : it) } : x));

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Hizmetler</h2>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
      </div>

      <div className="bg-white/5 border border-white/10 p-4 font-mono text-[10px] text-white/50 uppercase tracking-widest">
        💡 Not: Başlıklar, hizmet isimleri ve içerikleri artık <b>"ÇELİRİLER (Translations)"</b> sekmesinden dile göre (Türkçe/İngilizce) yönetilmektedir. Burada yapılan değişiklikler sadece yapısal (id, renk) ve varsayılan içerik amaçlıdır.
      </div>

      {/* Başlık ayarları */}
      <PanelSection title="Başlık">
        <div className="grid md:grid-cols-3 gap-4">
          <TextInput label="Etiket" value={heading.label} onChange={v => setHeading(h => ({ ...h, label: v }))} placeholder="Hizmetler" />
          <TextInput label="Başlık" value={heading.title} onChange={v => setHeading(h => ({ ...h, title: v }))} placeholder="Ne Yapıyoruz" />
          <TextInput label="Alt yazı" value={heading.subtitle} onChange={v => setHeading(h => ({ ...h, subtitle: v }))} placeholder="Açıklama..." />
        </div>
      </PanelSection>

      {/* Bayraklar */}
      <PanelSection title={`Hizmet Bayrakları (${services.length})`}>
        <div className="space-y-6">
          <p className="font-mono text-[9px] text-white/25 uppercase tracking-widest">⠿ tutamacından sürükleyip bırakarak sırala — sonra Kaydet'e bas</p>
          <DraggableList
            items={services}
            onReorder={setServices}
            className="space-y-6"
            renderItem={(svc, si) => (
            <div className="border border-white/8 rounded-md p-4 space-y-4 hover:border-white/15 transition-colors">
              {/* Bayrak başlığı */}
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-white/30 uppercase tracking-widest">
                  Bayrak {si + 1}
                </span>
                <Btn onClick={() => removeService(svc.id)} variant="danger" small>🗑 Sil</Btn>
              </div>

              {/* Bayrak alanları */}
              <div className="grid md:grid-cols-2 gap-4">
                <TextInput label="Başlık" value={svc.title} onChange={v => updateService(svc.id, 'title', v)} />
                <TextInput label="Alt başlık" value={svc.subtitle} onChange={v => updateService(svc.id, 'subtitle', v)} />
              </div>

              {/* Renk */}
              <div className="flex items-center gap-3">
                <ColorInput label="Renk" value={svc.color} onChange={v => updateService(svc.id, 'color', v)} />
                {/* Önizleme çubuğu */}
                <div className="flex-1 h-[3px] rounded-full self-end mb-4" style={{ background: svc.color, opacity: 0.6 }} />
              </div>

              {/* Maddeler */}
              <div>
                <FieldLabel text="Hizmet Maddeleri" />
                <div className="space-y-2 mt-2">
                  {(svc.items || []).map((item, ii) => (
                    <div key={ii} className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-white/20 w-5 flex-shrink-0">{ii + 1}.</span>
                      <TextInput value={item} onChange={v => updateItem(svc.id, ii, v)} />
                      <Btn onClick={() => removeItem(svc.id, ii)} variant="ghost" small title="Maddeyi sil">✕</Btn>
                    </div>
                  ))}
                </div>
                <button onClick={() => addItem(svc.id)}
                  className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 hover:text-white/80 transition-all border border-dashed border-white/15 hover:border-white/40 rounded-md px-3 py-1.5 hover:bg-white/[0.03]">
                  + Madde Ekle
                </button>
              </div>
            </div>
            )}
          />

          {/* Yeni bayrak */}
          <button onClick={addService}
            className="w-full font-mono text-[9px] uppercase tracking-[0.25em] text-white/30 hover:text-white/60 transition-colors border border-dashed border-white/10 hover:border-white/25 py-4">
            + Yeni Bayrak Ekle
          </button>
        </div>
      </PanelSection>
    </div>
  );
};

// ─── Medya ────────────────────────────────────────────────────────
const MediaTab = () => {
  const { token } = useAuth();
  const [media, setMedia] = useState({ images: [], videos: [] });
  const [toast, setToast] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const data = await apiFetch('/admin/media', {}, token); setMedia(data); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/admin/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Yükleme başarısız');
      const data = await res.json();
      setToast({ message: `Yüklendi: ${data.path}`, type: 'success' }); load();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setUploading(false); }
  };

  const del = async (path) => {
    if (!window.confirm('Silmek istediğine emin misin?')) return;
    try { await apiFetch(`/admin/media?path=${encodeURIComponent(path)}`, { method: 'DELETE' }, token); setToast({ message: 'Silindi', type: 'success' }); load(); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const copy = (path) => { navigator.clipboard.writeText(path); setToast({ message: 'Yol kopyalandı!', type: 'success' }); };
  const allFiles = [...media.images.map(p => ({ path: p, type: 'image' })), ...media.videos.map(p => ({ path: p, type: 'video' }))];

  return (
    <div>
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Medya <span className="text-white/20">({allFiles.length})</span></h2>
        <div>
          <input ref={fileRef} type="file" accept="image/*,video/mp4,video/mov" onChange={upload} className="hidden" />
          <Btn onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? 'Yükleniyor...' : '+ Dosya Yükle'}</Btn>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {allFiles.map(({ path, type }) => (
          <div key={path} className="group relative border border-white/5 hover:border-white/20 transition-colors">
            {type === 'image' ? <img src={path} alt="" className="w-full aspect-square object-cover" /> :
              <div className="w-full aspect-square bg-white/5 flex items-center justify-center"><span className="font-mono text-xs text-white/30">VIDEO</span></div>}
            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
              <p className="font-mono text-[9px] text-white/60 text-center break-all">{path}</p>
              <Btn onClick={() => copy(path)} variant="secondary" small>Yolu Kopyala</Btn>
              <Btn onClick={() => del(path)} variant="danger" small>Sil</Btn>
            </div>
          </div>
        ))}
        {allFiles.length === 0 && <p className="col-span-4 font-mono text-xs text-white/20 py-8 text-center">Henüz medya yok.</p>}
      </div>
    </div>
  );
};

// ─── Ayarlar ──────────────────────────────────────────────────────
const SettingsTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token)
      .then(setSettings)
      .catch(e => setToast({ message: e.message, type: 'error' }));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'Kaydedildi! Sayfayı yenile.', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));

  if (!settings) return <p className="font-mono text-xs text-white/30">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Site Ayarları</h2>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
      </div>

      <PanelSection title="Navigasyon">
        <div className="grid md:grid-cols-2 gap-4">
          <TextInput label="Font boyutu" value={settings.nav_font_size ?? '12px'} onChange={s('nav_font_size')} placeholder="12px" />
          <TextInput label="Letter spacing" value={settings.nav_letter_spacing ?? '0.3em'} onChange={s('nav_letter_spacing')} placeholder="0.3em" />
          <TextInput label="Logo ölçeği (normal)" value={String(settings.nav_logo_scale ?? 0.35)} onChange={v => s('nav_logo_scale')(Number(v))} type="number" placeholder="0.35" />
          <TextInput label="Logo ölçeği (scroll)" value={String(settings.nav_logo_scale_scrolled ?? 0.20)} onChange={v => s('nav_logo_scale_scrolled')(Number(v))} type="number" placeholder="0.20" />
        </div>
        <TextInput label="Logo 3D model yolu" value={settings.nav_logo_src ?? '/knoxopia_logo_3d.glb'} onChange={s('nav_logo_src')} placeholder="/knoxopia_logo_3d.glb" />
      </PanelSection>


      <PanelSection title="İletişim">
        <TextInput label="E-posta" value={settings.contact_email} onChange={s('contact_email')} placeholder="info@knoxopia.com" />
      </PanelSection>

      <PanelSection title="Galeri Bölümü Yazıları">
        <div className="space-y-4">
          <TextInput label="Üst etiket" value={settings.gallery_label ?? 'our philosophy'} onChange={s('gallery_label')} placeholder="our philosophy" />
          <TextInput label="Ana Başlık" value={settings.gallery_title ?? 'We are KNOXOPIA srg'} onChange={s('gallery_title')} placeholder="We are KNOXOPIA srg" />
          <TextArea label="Açıklama" value={settings.gallery_desc ?? 'During your training...'} onChange={s('gallery_desc')} rows={3} placeholder="During your training..." />
          <TextInput label="Arkaplan Yazısı" value={settings.gallery_bg_text ?? "KNOXOPIA SRG'_"} onChange={s('gallery_bg_text')} placeholder="KNOXOPIA SRG'_" />
        </div>
      </PanelSection>
    </div>
  );
};

// ─── İçerikler ────────────────────────────────────────────────────
const ContentTab = () => {
  const { token } = useAuth();
  const [content, setContent] = useState({});
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState('');

  useEffect(() => {
    apiFetch('/admin/content', {}, token)
      .then(setContent)
      .catch(e => setToast({ message: e.message, type: 'error' }));
  }, [token]);

  const update = async (section, key, value) => {
    const k = `${section}.${key}`; setSaving(k);
    try {
      await apiFetch('/admin/content', { method: 'PUT', body: JSON.stringify({ section, key, value }) }, token);
      setToast({ message: 'Kaydedildi', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(''); }
  };

  const setField = (section, key, val) => setContent(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: val } }));

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Sayfa İçerikleri</h2>
      {Object.entries(content).map(([section, fields]) => (
        <PanelSection key={section} title={section}>
          {Object.entries(fields).map(([key, value]) => (
            <div key={key} className="flex items-end gap-3">
              <div className="flex-1">
                <TextInput label={key} value={value} onChange={v => setField(section, key, v)} />
              </div>
              <Btn onClick={() => update(section, key, content[section][key])} disabled={saving === `${section}.${key}`} small>
                {saving === `${section}.${key}` ? '...' : 'Kaydet'}
              </Btn>
            </div>
          ))}
        </PanelSection>
      ))}
      {Object.keys(content).length === 0 && <p className="font-mono text-xs text-white/20 py-8 text-center">Henüz içerik yok.</p>}
    </div>
  );
};

// ─── Galeri (Büyük bir dosya idi, içeriğini dışa aldık veya sildik) ─────

// ─── Çeviriler ──────────────────────────────────────────────────
// ─── Çeviriler ──────────────────────────────────────────────────
const TranslationsTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [translationsLocal, setTranslationsLocal] = useState({ tr: {}, en: {} });
  const [activeLang, setActiveLang] = useState('tr'); // 'tr' or 'en'
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token)
      .then(data => {
        setSettings(data);
        // Eğer veritabanında hiç yoksa varsayılanları kullan, varsa birleştir
        const dbTrans = data.app_translations || { tr: {}, en: {} };

        // Derin birleştirme (Deep Merge) yaparak her iki tarafın eksiklerini kapat
        const merged = JSON.parse(JSON.stringify(defaultTranslations));
        Object.keys(dbTrans).forEach(lang => {
          if (!merged[lang]) merged[lang] = dbTrans[lang];
          else {
            Object.keys(dbTrans[lang]).forEach(sec => {
              if (!merged[lang][sec]) merged[lang][sec] = dbTrans[lang][sec];
              // Stats/Features array ise direkt al
              else if (Array.isArray(dbTrans[lang][sec])) merged[lang][sec] = dbTrans[lang][sec];
              else if (typeof dbTrans[lang][sec] === 'object') {
                Object.assign(merged[lang][sec], dbTrans[lang][sec]);
              } else {
                merged[lang][sec] = dbTrans[lang][sec];
              }
            });
          }
        });

        setTranslationsLocal(merged);
      })
      .catch(e => setToast({ message: e.message, type: 'error' }));
  }, [token]);

  const handleFieldChange = (section, key, value, index = null) => {
    setTranslationsLocal(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (index !== null) {
        // Array item update (e.g. features[0].title)
        if (!next[activeLang][section][key]) next[activeLang][section][key] = [];
        next[activeLang][section][index][key] = value;
      } else {
        if (!next[activeLang][section]) next[activeLang][section] = {};
        next[activeLang][section][key] = value;
      }
      return next;
    });
  };

  const handleArrayFieldChange = (section, index, key, value) => {
    setTranslationsLocal(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (next[activeLang][section] && next[activeLang][section][index]) {
        next[activeLang][section][index][key] = value;
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updatedSettings = { ...settings, app_translations: translationsLocal };
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(updatedSettings) }, token);
      setToast({ message: 'Çeviriler başarıyla güncellendi!', type: 'success' });
    } catch (e) {
      setToast({ message: 'Hata: ' + e.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="font-mono text-xs text-white/30 p-12 text-center">Yükleniyor...</p>;

  const currentLangData = translationsLocal[activeLang] || {};

  return (
    <div className="space-y-8">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      {/* Header & Global Actions */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-white/10">
        <div>
          <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 flex items-center gap-2 mb-2">
            <FileCode className="w-5 h-5 text-white/40" /> Dil ve Metin Yönetimi
          </h2>
          <p className="text-white/30 text-[10px] uppercase tracking-widest">Sitedeki tüm bölümlerin metinlerini buradan kolayca düzenleyin.</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Dil Seçici */}
          <div className="flex p-1 bg-black/40 border border-white/5 rounded-sm">
            {['tr', 'en'].map(l => (
              <button
                key={l}
                onClick={() => setActiveLang(l)}
                className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-all ${activeLang === l ? 'bg-white/10 text-white shadow-lg' : 'text-white/30 hover:text-white/60'
                  }`}
              >
                {l === 'tr' ? 'Türkçe' : 'English'}
              </button>
            ))}
          </div>
          <Btn onClick={handleSave} disabled={saving}>{saving ? 'Bekleyin...' : 'Hepsini Kaydet'}</Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-12">
        {Object.keys(defaultTranslations[activeLang]).map(sectionKey => {
          const sectionData = currentLangData[sectionKey] || {};
          const isArray = Array.isArray(sectionData);

          return (
            <PanelSection key={sectionKey} title={sectionKey.toUpperCase()}>
              {isArray ? (
                // Array fields (Program Features etc.)
                <div className="space-y-8">
                  {sectionData.map((item, idx) => (
                    <div key={idx} className="p-4 bg-white/[0.02] border border-white/5 space-y-4">
                      <span className="font-mono text-[9px] text-white/20 uppercase tracking-widest">Öğe #{idx + 1}</span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.keys(item).map(itemKey => (
                          <TextInput
                            key={itemKey}
                            label={itemKey}
                            value={item[itemKey]}
                            onChange={(val) => handleArrayFieldChange(sectionKey, idx, itemKey, val)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Standard section object
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  {Object.keys(defaultTranslations[activeLang][sectionKey]).map(key => {
                    const value = sectionData[key] || '';
                    const isLong = value.length > 50 || key.includes('desc') || key.includes('para') || key.includes('text') || key.includes('heading');

                    if (isLong) {
                      return (
                        <div key={key} className="md:col-span-2">
                          <TextArea
                            label={key}
                            value={value}
                            onChange={(val) => handleFieldChange(sectionKey, key, val)}
                          />
                        </div>
                      );
                    }
                    return (
                      <TextInput
                        key={key}
                        label={key}
                        value={value}
                        onChange={(val) => handleFieldChange(sectionKey, key, val)}
                      />
                    );
                  })}
                </div>
              )}
            </PanelSection>
          );
        })}
      </div>

      <div className="pt-12 border-t border-white/5 flex justify-center">
        <Btn onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Değişiklikleri Yayınla'}</Btn>
      </div>
    </div>
  );
};

// ─── Başvurular ───────────────────────────────────────────────────
const ApplicationsTab = () => {
  const { token } = useAuth();
  const [apps, setApps] = useState([]);
  const [toast, setToast] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  const load = useCallback(async () => {
    try { const data = await apiFetch('/admin/applications', {}, token); setApps(data); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try { await apiFetch(`/admin/applications/${id}/status?status=${status}`, { method: 'PUT' }, token); setToast({ message: 'Güncellendi', type: 'success' }); load(); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const rejectAndDelete = async (id, email) => {
    if (!window.confirm(`"${email}" başvurusunu reddet ve kalıcı sil?\nBu e-posta ile yeniden başvurulabilecek.`)) return;
    setRejectingId(id);
    try {
      await apiFetch(`/admin/applications/${id}/reject`, { method: 'DELETE' }, token);
      setToast({ message: `Silindi — ${email} artık yeniden başvurabilir`, type: 'success' });
      load();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setRejectingId(null); }
  };

  const statusColor = { pending: 'text-yellow-400', approved: 'text-green-400', rejected: 'text-red-400' };

  return (
    <div>
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Başvurular <span className="text-white/20">({apps.length})</span></h2>
      </div>
      <div className="space-y-2">
        {apps.map(a => (
          <motion.div key={a.id} className="border border-white/5 p-4 hover:border-white/10 transition-colors" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-mono text-sm font-bold">{a.name}</p>
                <p className="font-mono text-xs text-white/40">{a.email}</p>
                {a.telegram && <p className="font-mono text-xs text-white/30">TG: {a.telegram}</p>}
                {a.portfolio_link && <a href={a.portfolio_link} target="_blank" rel="noreferrer" className="font-mono text-xs text-blue-400/70 hover:text-blue-400 transition-colors truncate block">{a.portfolio_link}</a>}
                <p className="font-mono text-[10px] text-white/20 mt-1">{new Date(a.created_at).toLocaleDateString('tr-TR')}</p>
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <span className={`font-mono text-[10px] uppercase tracking-wider ${statusColor[a.status] || 'text-white/40'}`}>{a.status}</span>
                <div className="flex gap-1">
                  {a.status !== 'approved' && <Btn onClick={() => updateStatus(a.id, 'approved')} small>Onayla</Btn>}
                  <Btn onClick={() => rejectAndDelete(a.id, a.email)} variant="danger" small disabled={rejectingId === a.id}>{rejectingId === a.id ? '...' : 'Reddet & Sil'}</Btn>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
        {apps.length === 0 && <p className="font-mono text-xs text-white/20 py-8 text-center">Başvuru yok.</p>}
      </div>
    </div>
  );
};


// ─── Galeri ───────────────────────────────────────────────────────
const GALLERY_ASPECT_OPTIONS = [
  { label: 'Dikey  3/4', value: 'aspect-[3/4]' },
  { label: 'Dikey  2/3', value: 'aspect-[2/3]' },
  { label: 'Kare   1/1', value: 'aspect-[1/1]' },
  { label: 'Yatay  4/3', value: 'aspect-[4/3]' },
  { label: 'Yatay  16/9', value: 'aspect-[16/9]' },
];

const GalleryTab = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | item obj
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const emptyForm = { src: '', type: 'image', aspect: 'aspect-[3/4]', order: 0 };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try { const data = await apiFetch('/admin/gallery', {}, token); setItems(data); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setEditing('new'); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item); };
  const f = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    if (!form.src.trim()) { setToast({ message: 'Görsel/video yolu zorunlu', type: 'error' }); return; }
    setLoading(true);
    try {
      if (editing === 'new') {
        await apiFetch('/admin/gallery', { method: 'POST', body: JSON.stringify(form) }, token);
      } else {
        await apiFetch(`/admin/gallery/${editing.id}`, { method: 'PUT', body: JSON.stringify(form) }, token);
      }
      setToast({ message: 'Kaydedildi', type: 'success' });
      setEditing(null); load();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Bu öğeyi silmek istediğine emin misin?')) return;
    try { await apiFetch(`/admin/gallery/${id}`, { method: 'DELETE' }, token); setToast({ message: 'Silindi', type: 'success' }); load(); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  // Sürükle-bırak sıralama — her öğenin order alanı mevcut endpoint'le güncellenir
  const reorder = async (next) => {
    setItems(next);
    try {
      await Promise.all(next.map((it, i) =>
        it.order !== i ? apiFetch(`/admin/gallery/${it.id}`, { method: 'PUT', body: JSON.stringify({ order: i }) }, token) : null
      ));
      setToast({ message: 'Sıralama kaydedildi', type: 'success' });
      load();
    } catch (e) { setToast({ message: e.message, type: 'error' }); load(); }
  };

  return (
    <div>
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
          Galeri <span className="text-white/20">({items.length})</span>
        </h2>
        <Btn onClick={openNew}>+ Yeni Öğe</Btn>
      </div>

      {/* Form */}
      <AnimatePresence>
        {editing && (
          <motion.div className="mb-8 border border-white/10 p-6 space-y-5"
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              {editing === 'new' ? 'Yeni Öğe' : 'Düzenle'}
            </h3>

            {/* Tür seçimi */}
            <div>
              <FieldLabel text="Tür" />
              <div className="flex gap-2">
                {['image', 'video'].map(t => (
                  <button key={t} onClick={() => f('type')(t)}
                    className={`font-mono text-[10px] px-4 py-2 border transition-colors ${form.type === t ? 'border-white text-white bg-white/10' : 'border-white/10 text-white/40 hover:border-white/30'
                      }`}>
                    {t === 'image' ? '🖼 Görsel' : '🎬 Video'}
                  </button>
                ))}
              </div>
            </div>

            {/* Kaynak yolu + önizleme + yükleme + kitaplık */}
            <MediaInput
              label={form.type === 'video' ? 'Video yolu' : 'Görsel yolu'}
              value={form.src}
              onChange={f('src')}
              filter={form.type === 'video' ? 'video' : 'image'}
              placeholder={form.type === 'video' ? '/media/videos/video.mp4' : '/media/images/foto.jpg'}
            />

            {/* Oran seçimi */}
            <div>
              <FieldLabel text="Görsel Oranı" />
              <div className="flex flex-wrap gap-2">
                {GALLERY_ASPECT_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => f('aspect')(opt.value)}
                    className={`font-mono text-[10px] px-3 py-2 border transition-colors ${form.aspect === opt.value
                      ? 'border-white text-white bg-white/10'
                      : 'border-white/10 text-white/40 hover:border-white/30 hover:text-white/70'
                      }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Oran önizleme */}
              <div className="mt-3 flex items-end gap-2">
                <div className={`bg-white/10 border border-white/10 w-16 ${form.aspect}`} />
                <span className="font-mono text-[10px] text-white/30">{form.aspect.replace('aspect-[', '').replace(']', '')}</span>
              </div>
            </div>

            <TextInput label="Sıra" value={String(form.order)} onChange={v => f('order')(Number(v))} type="number" placeholder="0" />

            <div className="flex gap-3 pt-2">
              <Btn onClick={save} disabled={loading}>{loading ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
              <Btn onClick={() => setEditing(null)} variant="secondary">İptal</Btn>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Liste — sürükle-bırak sıralamalı */}
      <p className="font-mono text-[9px] text-white/25 mb-2 uppercase tracking-widest">⠿ tutamacından sürükleyip bırakarak sırala — otomatik kaydedilir</p>
      <DraggableList
        items={items}
        onReorder={reorder}
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        renderItem={(item, index) => (
          <div className="group relative border border-white/5 rounded-md overflow-hidden hover:border-white/20 transition-colors">
            {/* Önizleme */}
            <div className={`w-full ${item.aspect || 'aspect-[3/4]'} overflow-hidden bg-white/5`}>
              {item.type === 'video' ? (
                <video src={item.src} className="w-full h-full object-cover" muted autoPlay loop playsInline />
              ) : (
                <img src={item.src} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            {/* Numara + oran etiketi */}
            <div className="px-2 py-1.5 flex items-center justify-between">
              <span className="font-mono text-[9px] text-white/30">#{index + 1}</span>
              <span className="font-mono text-[9px] text-white/20">
                {(item.aspect || 'aspect-[3/4]').replace('aspect-[', '').replace(']', '')}
              </span>
              {item.type === 'video' && <span className="font-mono text-[9px] text-white/30">VID</span>}
            </div>
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
              <Btn onClick={() => openEdit(item)} variant="secondary" small>✎ Düzenle</Btn>
              <Btn onClick={() => del(item.id)} variant="danger" small>🗑 Sil</Btn>
            </div>
          </div>
        )}
      />
      {items.length === 0 && (
        <p className="font-mono text-xs text-white/20 py-8 text-center">Henüz galeri öğesi yok.</p>
      )}
    </div>
  );
};

// ─── Dashboard ────────────────────────────────────────────────────

// ─── Content Blocks Tab ───────────────────────────────────────────────────────
const ContentBlocksTab = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null); // selected project id
  const [blocks, setBlocks] = useState([]);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // index of block being edited
  const [form, setForm] = useState({ type: 'media_text', layout: 'left', title: '', text: '', media_url: '', order: 0 });

  useEffect(() => {
    apiFetch('/admin/projects', {}, token).then(setProjects).catch(() => { });
  }, [token]);

  const selectProject = (p) => {
    setSelected(p);
    setBlocks(Array.isArray(p.blocks) ? [...p.blocks] : []);
    setEditIdx(null);
  };

  const openNew = () => {
    setForm({ type: 'media_text', layout: 'left', title: '', text: '', media_url: '', order: blocks.length });
    setEditIdx('new');
  };

  const openEdit = (idx) => {
    setForm({ ...blocks[idx] });
    setEditIdx(idx);
  };

  const saveBlock = () => {
    const newBlocks = [...blocks];
    const block = { ...form, id: form.id || String(Date.now()) };
    if (editIdx === 'new') newBlocks.push(block);
    else newBlocks[editIdx] = block;
    newBlocks.sort((a, b) => a.order - b.order);
    setBlocks(newBlocks);
    setEditIdx(null);
  };

  const deleteBlock = (idx) => {
    const newBlocks = blocks.filter((_, i) => i !== idx);
    setBlocks(newBlocks);
  };

  const moveBlock = (idx, dir) => {
    const newBlocks = [...blocks];
    const target = idx + dir;
    if (target < 0 || target >= newBlocks.length) return;
    [newBlocks[idx], newBlocks[target]] = [newBlocks[target], newBlocks[idx]];
    newBlocks.forEach((b, i) => b.order = i);
    setBlocks(newBlocks);
  };

  const saveToProject = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiFetch(`/admin/projects/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({ blocks }),
      }, token);
      setToast({ message: 'Bloklar kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const TYPE_LABELS = { text: 'Sadece Metin', image: 'Sadece Görsel', video: 'Sadece Video', media_text: 'Medya + Metin' };

  return (
    <div className="space-y-8">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">İçerik Blokları</h2>

      {/* Proje Seç */}
      <div>
        <FieldLabel text="Proje Seç" />
        <div className="flex flex-wrap gap-2 mt-2">
          {projects.map(p => (
            <button key={p.id} onClick={() => selectProject(p)}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${selected?.id === p.id ? 'border-white/40 text-white bg-white/5' : 'border-white/10 text-white/40 hover:border-white/30'
                }`}>
              {p.title}
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">
              {selected.title} — {blocks.length} blok
            </p>
            <div className="flex gap-3">
              <Btn onClick={openNew}>+ Blok Ekle</Btn>
              <Btn onClick={saveToProject} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
            </div>
          </div>

          {/* Block Edit Form */}
          <AnimatePresence>
            {editIdx !== null && (
              <motion.div className="border border-white/10 p-6 space-y-4"
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {editIdx === 'new' ? 'Yeni Blok' : 'Bloğu Düzenle'}
                </h3>

                {/* Type */}
                <div>
                  <FieldLabel text="Blok Tipi" />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(TYPE_LABELS).map(([val, label]) => (
                      <button key={val} onClick={() => setForm(f => ({ ...f, type: val }))}
                        className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${form.type === val ? 'border-white/40 text-white' : 'border-white/10 text-white/40'
                          }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Layout (medya tarafı) */}
                {(form.type === 'media_text') && (
                  <div>
                    <FieldLabel text="Medya Konumu" />
                    <div className="flex gap-2 mt-2">
                      {['left', 'right'].map(v => (
                        <button key={v} onClick={() => setForm(f => ({ ...f, layout: v }))}
                          className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${form.layout === v ? 'border-white/40 text-white' : 'border-white/10 text-white/40'
                            }`}>
                          {v === 'left' ? '← Sol' : 'Sağ →'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Media URL */}
                {(form.type === 'media_text' || form.type === 'image' || form.type === 'video') && (
                  <MediaInput
                    label={form.type === 'video' ? 'Video URL' : 'Görsel URL'}
                    value={form.media_url ?? ''}
                    onChange={v => setForm(f => ({ ...f, media_url: v }))}
                    filter={form.type === 'video' ? 'video' : form.type === 'image' ? 'image' : 'all'}
                    placeholder="https://... veya /media/images/..."
                  />
                )}

                {/* Title */}
                {(form.type === 'media_text' || form.type === 'text') && (
                  <TextInput label="Başlık (opsiyonel)" value={form.title ?? ''} onChange={v => setForm(f => ({ ...f, title: v }))} placeholder="Bölüm başlığı..." />
                )}

                {/* Text */}
                {(form.type === 'media_text' || form.type === 'text') && (
                  <div>
                    <FieldLabel text="Metin" />
                    <textarea
                      value={form.text ?? ''}
                      onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                      rows={5}
                      placeholder="İçerik metni..."
                      className="w-full bg-transparent border border-white/10 px-3 py-2 font-mono text-sm text-white/80 focus:outline-none focus:border-white/30 resize-none mt-1"
                    />
                  </div>
                )}

                <TextInput label="Sıra" value={String(form.order)} onChange={v => setForm(f => ({ ...f, order: Number(v) }))} type="number" placeholder="0" />

                <div className="flex gap-3">
                  <Btn onClick={saveBlock}>Tamam</Btn>
                  <Btn onClick={() => setEditIdx(null)} variant="secondary">İptal</Btn>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Block List */}
          <div className="space-y-2">
            {blocks.length === 0 && (
              <p className="font-mono text-xs text-white/20 py-6 text-center">Henüz blok yok. + Blok Ekle ile başla.</p>
            )}
            {blocks.map((b, i) => (
              <motion.div key={b.id || i} className="flex items-start gap-4 border border-white/5 p-4 hover:border-white/10 transition-colors"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* Preview */}
                <div className="flex-shrink-0 w-20 h-14 bg-white/5 border border-white/10 overflow-hidden">
                  {(b.type === 'image' || b.type === 'media_text') && b.media_url && (
                    <img src={b.media_url} alt="" className="w-full h-full object-cover" />
                  )}
                  {b.type === 'video' && <div className="w-full h-full flex items-center justify-center text-white/20 text-xs font-mono">VIDEO</div>}
                  {b.type === 'text' && <div className="w-full h-full flex items-center justify-center text-white/20 text-xs font-mono">TXT</div>}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/30 border border-white/10 px-1.5 py-0.5">
                      {TYPE_LABELS[b.type]}
                    </span>
                    {b.type === 'media_text' && (
                      <span className="font-mono text-[9px] uppercase tracking-widest text-white/20">
                        {b.layout === 'left' ? '← Sol' : 'Sağ →'}
                      </span>
                    )}
                  </div>
                  {b.title && <p className="font-mono text-xs text-white/60 truncate">{b.title}</p>}
                  {b.text && <p className="font-mono text-[10px] text-white/30 truncate">{b.text}</p>}
                </div>
                {/* Actions */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <div className="flex gap-1">
                    <button onClick={() => moveBlock(i, -1)} disabled={i === 0}
                      className="px-2 py-1 font-mono text-[10px] border border-white/10 text-white/30 hover:text-white disabled:opacity-20">↑</button>
                    <button onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1}
                      className="px-2 py-1 font-mono text-[10px] border border-white/10 text-white/30 hover:text-white disabled:opacity-20">↓</button>
                  </div>
                  <div className="flex gap-1">
                    <Btn onClick={() => openEdit(i)} variant="secondary" small>Düzenle</Btn>
                    <Btn onClick={() => deleteBlock(i)} variant="danger" small>Sil</Btn>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const STAR_COLORS = ['#E2FF00', '#9999FF', '#FF4444', '#E87D0D', '#0172CE', '#31A8FF', '#FF9A00', '#00FF88', '#FF00FF'];

const ToolsetTab = () => {
  const { token } = useAuth();
  const [tools, setTools] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const emptyForm = () => ({
    name: '', title: '', stars: 5, starColor: '#E2FF00',
    glowColor: 'rgba(125,190,255,0.6)', innerGradient: 'linear-gradient(145deg,#60496e8c 0%,#71C4FF44 100%)',
    logoUrl: '', year: '', description: '',
    details: [{ label: 'Kullanım Alanı', value: '' }, { label: 'Deneyim', value: '' }, { label: 'Öne Çıkan', value: '' }],
    tags: '', order: 0,
    cardHeight: '260',
    cardWidth: '186',
  });

  const [form, setForm] = useState(emptyForm());
  const f = (key) => (val) => setForm(p => ({ ...p, [key]: val }));

  const load = useCallback(async () => {
    try { const data = await apiFetch('/admin/tools', {}, token); setTools(data); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm()); setEditing('new'); };
  const openEdit = (t) => {
    setForm({
      ...t, tags: Array.isArray(t.tags) ? t.tags.join(', ') : (t.tags || ''),
      details: t.details?.length ? t.details : [{ label: 'Kullanım Alanı', value: '' }, { label: 'Deneyim', value: '' }, { label: 'Öne Çıkan', value: '' }],
      cardHeight: t.cardHeight ?? '260',
      cardWidth: t.cardWidth ?? '186',
    });
    setEditing(t);
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean), order: Number(form.order) };
      if (editing === 'new') await apiFetch('/admin/tools', { method: 'POST', body: JSON.stringify(payload) }, token);
      else await apiFetch(`/admin/tools/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) }, token);
      setToast({ message: 'Kaydedildi', type: 'success' }); setEditing(null); load();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Silmek istediğine emin misin?')) return;
    try { await apiFetch(`/admin/tools/${id}`, { method: 'DELETE' }, token); setToast({ message: 'Silindi', type: 'success' }); load(); }
    catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const setDetail = (i, key, val) => {
    const d = [...form.details]; d[i] = { ...d[i], [key]: val }; setForm(p => ({ ...p, details: d }));
  };

  return (
    <div className="space-y-8">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Toolset <span className="text-white/20">({tools.length})</span></h2>
        <Btn onClick={openNew}>+ Yeni Araç</Btn>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div className="border border-white/10 p-6 space-y-5"
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{editing === 'new' ? 'Yeni Araç' : 'Düzenle'}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="İsim" value={form.name} onChange={f('name')} placeholder="After Effects" />
              <TextInput label="Kategori" value={form.title} onChange={f('title')} placeholder="Motion & VFX" />
              <TextInput label="Logo URL" value={form.logoUrl} onChange={f('logoUrl')} placeholder="/media/images/ae.png" />
              <TextInput label="Yıl" value={form.year} onChange={f('year')} placeholder="2019—" />
              <TextInput label="Sıra" value={String(form.order)} onChange={v => f('order')(Number(v))} type="number" placeholder="0" />
              <TextInput label="Kart Yüksekliği (px)" value={form.cardHeight ?? '260'} onChange={v => f('cardHeight')(v)} placeholder="260" />
              <TextInput label="Kart Genişliği (px)" value={form.cardWidth ?? '186'} onChange={v => f('cardWidth')(v)} placeholder="186" />
            </div>

            <div>
              <FieldLabel text="Yıldız (1-5)" />
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => f('stars')(n)}
                    className="w-8 h-8 font-mono text-sm border transition-colors"
                    style={{ borderColor: form.stars >= n ? form.starColor : 'rgba(255,255,255,0.1)', color: form.stars >= n ? form.starColor : 'rgba(255,255,255,0.2)' }}>
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div>
              <FieldLabel text="Yıldız Rengi" />
              <div className="flex flex-wrap gap-2 mt-2">
                {STAR_COLORS.map(c => (
                  <button key={c} onClick={() => f('starColor')(c)}
                    className="w-7 h-7 rounded-full border-2 transition-all"
                    style={{ backgroundColor: c, borderColor: form.starColor === c ? '#fff' : 'transparent', boxShadow: form.starColor === c ? `0 0 8px ${c}` : 'none' }} />
                ))}
                <input type="color" value={form.starColor} onChange={e => f('starColor')(e.target.value)}
                  className="w-7 h-7 bg-transparent border border-white/10 cursor-pointer" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="Glow Rengi (rgba)" value={form.glowColor} onChange={f('glowColor')} placeholder="rgba(153,153,255,0.6)" />
              <div>
                <FieldLabel text="Arka Plan Renkleri" />
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex flex-col gap-1 items-center">
                    <input type="color"
                      value={form.innerGradient?.match(/#[0-9a-fA-F]{6}/g)?.[0] || '#1a1a2e'}
                      onChange={e => {
                        const c2 = form.innerGradient?.match(/#[0-9a-fA-F]{6}/g)?.[1] || '#0a0a0a';
                        f('innerGradient')(`linear-gradient(145deg, ${e.target.value}cc 0%, ${c2}88 100%)`);
                      }}
                      className="w-10 h-10 cursor-pointer border border-white/10 bg-transparent" />
                    <span className="font-mono text-[8px] text-white/30">Renk 1</span>
                  </div>
                  <span className="font-mono text-white/20 text-sm">→</span>
                  <div className="flex flex-col gap-1 items-center">
                    <input type="color"
                      value={form.innerGradient?.match(/#[0-9a-fA-F]{6}/g)?.[1] || '#0a0a0a'}
                      onChange={e => {
                        const c1 = form.innerGradient?.match(/#[0-9a-fA-F]{6}/g)?.[0] || '#1a1a2e';
                        f('innerGradient')(`linear-gradient(145deg, ${c1}cc 0%, ${e.target.value}88 100%)`);
                      }}
                      className="w-10 h-10 cursor-pointer border border-white/10 bg-transparent" />
                    <span className="font-mono text-[8px] text-white/30">Renk 2</span>
                  </div>
                  <div className="flex-1 h-10 rounded" style={{
                    background: form.innerGradient || 'linear-gradient(145deg, #1a1a2e 0%, #0a0a0a 100%)'
                  }} />
                </div>
              </div>
            </div>

            <div>
              <FieldLabel text="Açıklama" />
              <textarea value={form.description} onChange={e => f('description')(e.target.value)}
                rows={3} placeholder="Program hakkında açıklama..."
                className="w-full bg-transparent border border-white/10 px-3 py-2 font-mono text-sm text-white/80 focus:outline-none focus:border-white/30 resize-none mt-1" />
            </div>

            <div>
              <FieldLabel text="Detaylar" />
              <div className="space-y-2 mt-2">
                {form.details.map((d, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={d.label} onChange={e => setDetail(i, 'label', e.target.value)}
                      placeholder="Etiket" className="w-36 bg-transparent border border-white/10 px-2 py-1.5 font-mono text-xs text-white/70 focus:outline-none focus:border-white/30" />
                    <input value={d.value} onChange={e => setDetail(i, 'value', e.target.value)}
                      placeholder="Değer" className="flex-1 bg-transparent border border-white/10 px-2 py-1.5 font-mono text-xs text-white/70 focus:outline-none focus:border-white/30" />
                  </div>
                ))}
                <button onClick={() => setForm(p => ({ ...p, details: [...p.details, { label: '', value: '' }] }))}
                  className="font-mono text-[9px] uppercase tracking-widest text-white/30 hover:text-white/60 border border-white/10 px-2 py-1">+ Satır Ekle</button>
              </div>
            </div>

            <TextInput label="Etiketler (virgülle)" value={form.tags} onChange={f('tags')} placeholder="Motion, VFX, Compositing" />

            <div className="flex gap-3 pt-2">
              <Btn onClick={save} disabled={loading}>{loading ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
              <Btn onClick={() => setEditing(null)} variant="secondary">İptal</Btn>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-2">
        {tools.map(t => (
          <motion.div key={t.id} className="flex items-center gap-4 border border-white/5 p-4 hover:border-white/10 transition-colors"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.starColor, boxShadow: `0 0 6px ${t.starColor}` }} />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm font-bold truncate">{t.name}</p>
              <p className="font-mono text-xs text-white/30">{t.title} · {t.year}</p>
              <div className="flex gap-0.5 mt-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <span key={i} style={{ color: i <= t.stars ? t.starColor : 'rgba(255,255,255,0.1)', fontSize: 10 }}>★</span>
                ))}
              </div>
            </div>
            {t.logoUrl && <img src={t.logoUrl} alt={t.name} className="w-8 h-8 object-contain flex-shrink-0" />}
            <div className="flex gap-2 flex-shrink-0">
              <Btn onClick={() => openEdit(t)} variant="secondary" small>Düzenle</Btn>
              <Btn onClick={() => del(t.id)} variant="danger" small>Sil</Btn>
            </div>
          </motion.div>
        ))}
        {tools.length === 0 && <p className="font-mono text-xs text-white/20 py-8 text-center">Henüz araç yok.</p>}
      </div>
    </div>
  );
};

const GridScanTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token).then(setSettings).catch(() => { });
  }, [token]);

  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'GridScan ayarları kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="font-mono text-xs text-white/30 py-8 text-center">Yükleniyor...</p>;

  return (
    <div className="space-y-6">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">GridScan Arkaplan Ayarları</h2>
          <p className="text-[10px] font-mono text-white/20 uppercase tracking-widest mt-1">Projeler bölümündeki ızgara animasyonu</p>
        </div>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Hepsini Kaydet'}</Btn>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Temel Görünüm */}
        <PanelSection title="🎨 Renkler & Opaklık">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel text="Çizgi Rengi" />
                <div className="flex items-center gap-3">
                  <input type="color" value={settings.gridscan_lines_color ?? '#392e4e'} onChange={e => s('gridscan_lines_color')(e.target.value)}
                    className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
                  <span className="font-mono text-xs text-white/50">{settings.gridscan_lines_color ?? '#392e4e'}</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Tarama Rengi" />
                <div className="flex items-center gap-3">
                  <input type="color" value={settings.gridscan_scan_color ?? '#FF9FFC'} onChange={e => s('gridscan_scan_color')(e.target.value)}
                    className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" />
                  <span className="font-mono text-xs text-white/50">{settings.gridscan_scan_color ?? '#FF9FFC'}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <FieldLabel text="Genel Opaklık" />
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="1" step="0.05" value={settings.gridscan_opacity ?? 0.4} onChange={e => s('gridscan_opacity')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-8">{settings.gridscan_opacity ?? 0.4}</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Tarama Opaklığı" />
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="1" step="0.05" value={settings.gridscan_scan_opacity ?? 0.2} onChange={e => s('gridscan_scan_opacity')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-8">{settings.gridscan_scan_opacity ?? 0.2}</span>
                </div>
              </div>
            </div>
          </div>
        </PanelSection>

        {/* Izgara Yapısı */}
        <PanelSection title="📐 Izgara Yapısı">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <FieldLabel text="Izgara Ölçeği" />
                <span className="text-[9px] font-mono text-white/20 block mb-1">Daha küçük değer = daha sık çizgiler</span>
                <div className="flex items-center gap-3">
                  <input type="range" min="0.01" max="1" step="0.01" value={settings.gridscan_grid_scale ?? 0.15} onChange={e => s('gridscan_grid_scale')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-10">{settings.gridscan_grid_scale ?? 0.15}</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Çizgi Kalınlığı" />
                <div className="flex items-center gap-3">
                  <input type="range" min="0.1" max="5" step="0.1" value={settings.gridscan_line_thickness ?? 0.8} onChange={e => s('gridscan_line_thickness')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-10">{settings.gridscan_line_thickness ?? 0.8}px</span>
                </div>
              </div>
            </div>

            <div>
              <FieldLabel text="Çizgi Stili" />
              <div className="flex gap-2">
                {['solid', 'dashed', 'dotted'].map(style => (
                  <button key={style} onClick={() => s('gridscan_line_style')(style)}
                    className={`font-mono text-[10px] px-4 py-2 border transition-colors ${(settings.gridscan_line_style ?? 'solid') === style ? 'border-white text-white bg-white/10' : 'border-white/10 text-white/40 hover:border-white/30'}`}>
                    {style.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </PanelSection>

        {/* Animasyon */}
        <PanelSection title="⚡ Animasyon Ayarları">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <FieldLabel text="Tarama Süresi (sn)" />
                <span className="text-[9px] font-mono text-white/20 block mb-1">Taramamanın hızı</span>
                <div className="flex items-center gap-3">
                  <input type="range" min="0.5" max="10" step="0.5" value={settings.gridscan_scan_duration ?? 4} onChange={e => s('gridscan_scan_duration')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-10">{settings.gridscan_scan_duration ?? 4}s</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Gecikme (sn)" />
                <span className="text-[9px] font-mono text-white/20 block mb-1">İki tarama arası bekleme</span>
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="10" step="0.5" value={settings.gridscan_scan_delay ?? 3} onChange={e => s('gridscan_scan_delay')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-10">{settings.gridscan_scan_delay ?? 3}s</span>
                </div>
              </div>
            </div>

            <div>
              <FieldLabel text="Tarama Yönü" />
              <div className="flex gap-2">
                {['forward', 'backward', 'pingpong'].map(dir => (
                  <button key={dir} onClick={() => s('gridscan_scan_direction')(dir)}
                    className={`font-mono text-[10px] px-4 py-2 border transition-colors ${(settings.gridscan_scan_direction ?? 'pingpong') === dir ? 'border-white text-white bg-white/10' : 'border-white/10 text-white/40 hover:border-white/30'}`}>
                    {dir.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </PanelSection>

        {/* Post-Processing */}
        <PanelSection title="✨ Efektler (Post-Processing)">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <FieldLabel text="Glow (Bloom) Yoğunluğu" />
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="5" step="0.1" value={settings.gridscan_bloom_intensity ?? 0} onChange={e => s('gridscan_bloom_intensity')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-8">{settings.gridscan_bloom_intensity ?? 0}</span>
                </div>
              </div>
              <div>
                <FieldLabel text="Gürültü (Noise) Miktarı" />
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="0.1" step="0.005" value={settings.gridscan_noise_intensity ?? 0.01} onChange={e => s('gridscan_noise_intensity')(Number(e.target.value))} className="flex-1 accent-white" />
                  <span className="font-mono text-xs text-white/50 w-10">{settings.gridscan_noise_intensity ?? 0.01}</span>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-white/[0.03] border border-white/5 rounded-sm">
                <p className="font-mono text-[9px] text-white/40 leading-relaxed uppercase tracking-wider">
                    <span className="text-white/60">Not:</span> Bloom yoğunluğu 0'dan büyük olursa tarama çizgileri parlama efekti kazanır. Bu özellik performansı bir miktar etkileyebilir.
                </p>
            </div>
          </div>
        </PanelSection>
      </div>

      <div className="pt-6 border-t border-white/5 flex justify-end">
        <Btn onClick={save} disabled={saving}>{saving ? 'Bekleyin...' : 'Ayarları Yayınla'}</Btn>
      </div>
    </div>
  );
};

// ─── Proje Görünümü (detay sayfası düzeni) ────────────────────────
const ProjectViewTab = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [projectImages, setProjectImages] = useState([]); // içe aktarma için (kapak + ek görseller)
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/admin/projects', {}, token).then(setProjects).catch(() => { });
  }, [token]);

  const selectProject = (id) => {
    setSelectedId(id);
    const p = projects.find(x => x.id === id);
    if (!p) { setDetail(null); setProjectImages([]); return; }
    setDetail({
      ...DEFAULT_DETAIL,
      ...(p.detail || {}),
      images: Array.isArray(p.detail?.images) ? p.detail.images : [],
    });
    const imgs = [p.image, ...(Array.isArray(p.images) ? p.images : [])].filter(Boolean);
    setProjectImages([...new Set(imgs)]);
  };

  const d = (key) => (val) => setDetail(prev => ({ ...prev, [key]: val }));

  const updateImage = (i, key, val) => setDetail(prev => {
    const images = [...(prev.images || [])];
    images[i] = { ...images[i], [key]: val };
    return { ...prev, images };
  });
  const addImageRow = (url = '') => setDetail(prev => ({ ...prev, images: [...(prev.images || []), { url, caption: '', size: 60 }] }));
  const removeImageRow = (i) => setDetail(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }));
  const moveImage = (i, dir) => setDetail(prev => {
    const images = [...prev.images]; const j = i + dir;
    if (j < 0 || j >= images.length) return prev;
    [images[i], images[j]] = [images[j], images[i]];
    return { ...prev, images };
  });
  const importFromProject = () => setDetail(prev => {
    const existing = new Set((prev.images || []).map(x => x.url));
    const additions = projectImages.filter(u => !existing.has(u)).map(u => ({ url: u, caption: '', size: 60 }));
    if (additions.length === 0) { setToast({ message: 'Yeni görsel yok', type: 'error' }); return prev; }
    return { ...prev, images: [...(prev.images || []), ...additions] };
  });

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await apiFetch(`/admin/projects/${selectedId}`, { method: 'PUT', body: JSON.stringify({ detail }) }, token);
      setToast({ message: 'Görünüm ayarları kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">Proje Detay Görünümü</h2>
        <select
          value={selectedId}
          onChange={e => selectProject(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-md px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-white/40 min-w-[220px]"
        >
          <option value="">— Proje seç —</option>
          {projects.map(p => <option key={p.id} value={p.id} className="bg-[#111]">{p.title}</option>)}
        </select>
      </div>

      {!detail && (
        <p className="font-mono text-xs text-white/20 py-12 text-center border border-white/5 rounded-lg">
          Düzenlemek için yukarıdan bir proje seç.
        </p>
      )}

      {detail && (
        <>
          <div className="flex justify-end">
            <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
          </div>

          {/* ── VİDEO ── */}
          <PanelSection title="🎬 Video Görünümü">
            <div>
              <FieldLabel text="Video yönü" />
              <span className="font-mono text-[9px] text-white/20 block mb-2">Dikey → açıklama solda yan yana &nbsp;|&nbsp; Yatay → tam genişlik + altında açıklama</span>
              <SegControl
                options={[{ label: 'Yatay', value: 'horizontal' }, { label: 'Dikey', value: 'vertical' }]}
                value={detail.video_orientation}
                onChange={d('video_orientation')}
              />
            </div>

            {detail.video_orientation === 'horizontal' ? (
              <Slider label="Video genişliği" value={detail.video_width} min={40} max={100} unit="%" onChange={d('video_width')} />
            ) : (
              <Slider label="Video yüksekliği (dikey)" value={detail.video_height} min={40} max={95} unit="vh" onChange={d('video_height')} />
            )}

            <div className="grid md:grid-cols-2 gap-4 pt-1">
              <div className="space-y-3">
                <Toggle label="Video üzerine başlık göster" value={detail.show_video_title} onChange={d('show_video_title')} />
                <Toggle label="Neon glow çerçeve" value={detail.video_glow} onChange={d('video_glow')} />
              </div>
              {detail.show_video_title && (
                <Slider label="Başlık boyutu" value={detail.video_title_size} min={2} max={10} step={0.5} unit="rem" onChange={d('video_title_size')} />
              )}
            </div>
          </PanelSection>

          {/* ── FOTOĞRAFLAR ── */}
          <PanelSection title="🖼️ Fotoğraflar (dönüşümlü düzen)">
            <p className="font-mono text-[10px] text-white/30">1. foto sağ / açıklama sol → 2. foto sol / açıklama sağ … şeklinde sıralanır.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Toggle label="Sağ/sol dönüşümlü diz" value={detail.image_alternate} onChange={d('image_alternate')} />
              </div>
              <Slider label="Fotoğraflar arası boşluk" value={detail.image_gap} min={20} max={200} step={10} unit="px" onChange={d('image_gap')} />
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Görseller ({detail.images.length})</span>
              <div className="flex gap-2">
                <Btn onClick={importFromProject} variant="secondary" small>↓ Projeden içe aktar</Btn>
                <Btn onClick={() => addImageRow('')} variant="secondary" small>+ Satır ekle</Btn>
              </div>
            </div>

            <div className="space-y-4">
              {detail.images.length === 0 && (
                <p className="font-mono text-xs text-white/20 py-6 text-center">Henüz görsel yok — "Projeden içe aktar" ile mevcut fotoğrafları çek.</p>
              )}
              {detail.images.map((it, i) => (
                <div key={i} className="flex gap-4 border border-white/10 rounded-lg p-4 bg-white/[0.015]">
                  <div className="w-20 h-20 flex-shrink-0 bg-cover bg-center rounded-md border border-white/10 bg-[#111]"
                    style={{ backgroundImage: it.url ? `url(${it.url})` : undefined }} />
                  <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-white/30 w-8">#{i + 1}</span>
                      <span className="font-mono text-[9px] text-white/40">{i % 2 === 0 || !detail.image_alternate ? 'Foto sağ · açıklama sol' : 'Foto sol · açıklama sağ'}</span>
                    </div>
                    <MediaInput label="Görsel URL" value={it.url} onChange={v => updateImage(i, 'url', v)} filter="image" placeholder="https://... .jpg" />
                    <TextArea label="Açıklama" value={it.caption} onChange={v => updateImage(i, 'caption', v)} rows={2} placeholder="Bu fotoğrafın açıklaması..." />
                    <Slider label="Fotoğraf boyutu" value={it.size ?? 60} min={20} max={100} step={5} unit="%" onChange={v => updateImage(i, 'size', v)} />
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => moveImage(i, -1)} disabled={i === 0} className="font-mono text-xs text-white/40 hover:text-white disabled:opacity-20 px-2 py-1 border border-white/10 rounded">↑</button>
                    <button onClick={() => moveImage(i, 1)} disabled={i === detail.images.length - 1} className="font-mono text-xs text-white/40 hover:text-white disabled:opacity-20 px-2 py-1 border border-white/10 rounded">↓</button>
                    <button onClick={() => removeImageRow(i)} className="font-mono text-xs text-red-400/70 hover:text-red-400 px-2 py-1 border border-red-400/20 rounded mt-1">✕</button>
                  </div>
                </div>
              ))}
            </div>
          </PanelSection>

          <div className="pt-6 border-t border-white/5 flex justify-end">
            <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
          </div>
        </>
      )}
    </div>
  );
};

// ─── İletişim Bölümü (ContactSection metinleri + sosyal medya) ────
const CONTACT_SOCIALS = [
  { key: 'social_instagram', label: 'Instagram', placeholder: 'https://instagram.com/knoxopia' },
  { key: 'social_twitter', label: 'X / Twitter', placeholder: 'https://x.com/knoxopia' },
  { key: 'social_linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/knoxopia' },
  { key: 'social_behance', label: 'Behance', placeholder: 'https://behance.net/knoxopia' },
  { key: 'social_youtube', label: 'YouTube', placeholder: 'https://youtube.com/@knoxopia' },
  { key: 'social_telegram', label: 'Telegram', placeholder: 'https://t.me/knoxopia' },
  { key: 'social_tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@knoxopia' },
  { key: 'social_github', label: 'GitHub', placeholder: 'https://github.com/knoxopia' },
  { key: 'social_dribbble', label: 'Dribbble', placeholder: 'https://dribbble.com/knoxopia' },
  { key: 'social_imdb', label: 'IMDb', placeholder: 'https://www.imdb.com/title/...' },
];

const ContactTab = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiFetch('/admin/settings', {}, token).then(setSettings).catch(() => { });
  }, [token]);

  const s = (key) => (val) => setSettings(prev => ({ ...prev, [key]: val }));
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/settings', { method: 'PUT', body: JSON.stringify(settings) }, token);
      setToast({ message: 'İletişim ayarları kaydedildi!', type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="font-mono text-xs text-white/30 py-8 text-center">Yükleniyor...</p>;

  const SaveBar = () => (
    <div className="flex justify-end">
      <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
    </div>
  );

  return (
    <div className="space-y-8">
      <AnimatePresence>{toast && <Toast {...toast} onClose={() => setToast(null)} />}</AnimatePresence>

      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">İletişim Bölümü</h2>
        <Btn onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Btn>
      </div>

      {/* Başlık & üst metinler */}
      <PanelSection title="✍️ Başlık & Metinler">
        <TextInput label="Rozet (badge)" value={settings.contact_badge ?? ''} onChange={s('contact_badge')} placeholder="submit an application ( waiting list )" />
        <TextArea label="Ana başlık" value={settings.contact_heading ?? ''} onChange={s('contact_heading')} rows={2} placeholder="KNOXOPIA will introduce you..." />
        <TextInput label="Selamlama (form üstü)" value={settings.contact_greeting ?? ''} onChange={s('contact_greeting')} placeholder="HELLO, KNOXOPIA'_!" />
      </PanelSection>

      {/* Form alan etiketleri */}
      <PanelSection title="🧩 Form Alan Etiketleri">
        <div className="grid md:grid-cols-2 gap-4">
          <TextInput label="İsim etiketi" value={settings.contact_field_name ?? ''} onChange={s('contact_field_name')} placeholder="My name is" />
          <TextInput label="İsim placeholder" value={settings.contact_placeholder_name ?? ''} onChange={s('contact_placeholder_name')} placeholder="Your name" />
          <TextInput label="E-posta etiketi" value={settings.contact_field_email ?? ''} onChange={s('contact_field_email')} placeholder="My email" />
          <TextInput label="E-posta placeholder" value={settings.contact_placeholder_email ?? ''} onChange={s('contact_placeholder_email')} placeholder="your@email.com" />
          <TextInput label="Instagram etiketi" value={settings.contact_field_instagram ?? ''} onChange={s('contact_field_instagram')} placeholder="and my instagram" />
          <TextInput label="Portfolyo etiketi" value={settings.contact_field_portfolio ?? ''} onChange={s('contact_field_portfolio')} placeholder="Link to my portfolio" />
        </div>
      </PanelSection>

      {/* Gizlilik & buton */}
      <PanelSection title="✅ Gizlilik & Gönder Butonu">
        <TextArea label="Gizlilik onay metni" value={settings.contact_privacy_text ?? ''} onChange={s('contact_privacy_text')} rows={3} placeholder="I agree with the privacy policy..." />
        <TextInput label="Buton yazısı" value={settings.contact_btn_label ?? ''} onChange={s('contact_btn_label')} placeholder="Send" />
      </PanelSection>

      {/* Başarı mesajı */}
      <PanelSection title="🎉 Gönderim Sonrası Mesaj">
        <TextInput label="Başarı başlığı" value={settings.contact_success_title ?? ''} onChange={s('contact_success_title')} placeholder="Application Sent!" />
        <TextArea label="Başarı açıklaması" value={settings.contact_success_desc ?? ''} onChange={s('contact_success_desc')} rows={2} placeholder="Thank you for your interest..." />
      </PanelSection>

      {/* İletişim bilgileri */}
      <PanelSection title="📮 İletişim Bilgileri">
        <div className="grid md:grid-cols-2 gap-4">
          <TextInput label="E-posta adresi" value={settings.contact_email ?? ''} onChange={s('contact_email')} placeholder="knoxopia@gmail.com" />
          <TextInput label="Sorular için kullanıcı adı" value={settings.contact_questions_handle ?? ''} onChange={s('contact_questions_handle')} placeholder="@knoxopia" />
        </div>
      </PanelSection>

      {/* Sosyal medya */}
      <PanelSection title="🔗 Sosyal Medya Linkleri">
        <p className="font-mono text-[10px] text-white/30 mb-2">Boş bırakılan platformlar iletişim bölümünde gösterilmez.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {CONTACT_SOCIALS.map(f => (
            <TextInput key={f.key} label={f.label} value={settings[f.key] ?? ''} onChange={s(f.key)} placeholder={f.placeholder} />
          ))}
        </div>
      </PanelSection>

      <div className="pt-6 border-t border-white/5">
        <SaveBar />
      </div>
    </div>
  );
};

const TABS = [
  { id: 'projects', label: 'Projeler', group: 'İçerik' },
  { id: 'services', label: 'Hizmetler', group: 'İçerik' },
  { id: 'gallery', label: 'Galeri', group: 'İçerik' },
  { id: 'toolset', label: 'Toolset', group: 'İçerik' },
  { id: 'contentblocks', label: 'Bloklar', group: 'İçerik' },
  { id: 'applications', label: 'Başvurular', group: 'İçerik' },
  { id: 'contact', label: 'İletişim', group: 'İçerik' },
  { id: 'projectview', label: 'Proje Görünümü', group: 'Görünüm' },
  { id: 'hero', label: 'Hero', group: 'Görünüm' },
  { id: 'philosophy', label: 'Philosophy', group: 'Görünüm' },
  { id: 'splash', label: 'Splash', group: 'Görünüm' },
  { id: 'gridscan', label: 'GridScan', group: 'Görünüm' },
  { id: 'media', label: 'Medya', group: 'Sistem' },
  { id: 'content', label: 'İçerikler', group: 'Sistem' },
  { id: 'translations', label: 'Çeviriler', group: 'Sistem' },
  { id: 'settings', label: 'Ayarlar', group: 'Sistem' },
];

const TAB_GROUPS = ['İçerik', 'Görünüm', 'Sistem'];

const TAB_COMPONENTS = {
  projects: ProjectsTab,
  projectview: ProjectViewTab,
  contact: ContactTab,
  services: ServicesTab,
  splash: SplashTab,
  media: MediaTab,
  hero: HeroTab,
  philosophy: PhilosophyTab,
  settings: SettingsTab,
  gridscan: GridScanTab,
  content: ContentTab,
  gallery: GalleryTab,
  translations: TranslationsTab,
  applications: ApplicationsTab,
  toolset: ToolsetTab,
  contentblocks: ContentBlocksTab,
};

// ─── Canlı Önizleme Paneli ─────────────────────────────────────────
// Siteyi iframe'de gösterir; admin'de bir şey kaydedildiğinde otomatik yenilenir.
const PREVIEW_ROUTES = [
  { label: 'Ana Sayfa', path: '/' },
  { label: 'Projeler', path: '/projects' },
];

const PreviewPanel = ({ onClose }) => {
  const [route, setRoute] = useState('/');
  const [nonce, setNonce] = useState(0);
  const [device, setDevice] = useState('desktop'); // desktop | mobile
  const reloadTimer = useRef(null);

  // Kayıt olayını dinle → kısa debounce ile iframe'i yenile
  useEffect(() => {
    const onSaved = () => {
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(() => setNonce(n => n + 1), 600);
    };
    window.addEventListener('knx-admin-saved', onSaved);
    return () => { window.removeEventListener('knx-admin-saved', onSaved); clearTimeout(reloadTimer.current); };
  }, []);

  const src = `${route}${route.includes('?') ? '&' : '?'}preview=1&_=${nonce}`;

  return (
    <div className="flex flex-col h-full border-l border-white/10 bg-[#070707]">
      {/* Panel başlığı */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" title="Kayıtta otomatik yenilenir" />
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 flex-1">Canlı Önizleme</span>
        <Btn onClick={() => setNonce(n => n + 1)} variant="ghost" small title="Yenile">⟳</Btn>
        <Btn onClick={onClose} variant="ghost" small title="Kapat">✕</Btn>
      </div>
      {/* Rota + cihaz seçimi */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 flex-wrap">
        {PREVIEW_ROUTES.map(r => (
          <button key={r.path} onClick={() => setRoute(r.path)}
            className={`font-mono text-[9px] uppercase tracking-widest px-2.5 py-1 rounded border transition-all ${route === r.path ? 'border-white/60 text-white bg-white/10' : 'border-white/10 text-white/40 hover:border-white/30'}`}>
            {r.label}
          </button>
        ))}
        <div className="flex-1" />
        {[{ id: 'desktop', icon: '🖥' }, { id: 'mobile', icon: '📱' }].map(d => (
          <button key={d.id} onClick={() => setDevice(d.id)}
            className={`text-xs px-2 py-1 rounded border transition-all ${device === d.id ? 'border-white/60 bg-white/10' : 'border-white/10 opacity-40 hover:opacity-80'}`}
            title={d.id === 'desktop' ? 'Masaüstü görünümü' : 'Mobil görünümü'}>
            {d.icon}
          </button>
        ))}
      </div>
      {/* iframe */}
      <div className="flex-1 min-h-0 bg-black flex items-start justify-center overflow-auto">
        <iframe
          key={src}
          src={src}
          title="Site önizlemesi"
          className={`border-0 bg-black ${device === 'mobile' ? 'w-[390px] h-full mx-auto border-x border-white/10' : 'w-full h-full'}`}
        />
      </div>
      <p className="px-3 py-1.5 font-mono text-[8px] text-white/20 uppercase tracking-widest border-t border-white/5">
        Kaydet'e bastığında otomatik yenilenir
      </p>
    </div>
  );
};

// Tek bir menü öğesi — aktifken sol kenarda accent çizgisi
const NavItem = ({ tab, active, onClick }) => (
  <button onClick={onClick}
    className={`relative w-full text-left pl-4 pr-3 py-2 rounded-md font-mono text-xs uppercase tracking-[0.15em] transition-all duration-200 ${active ? 'text-white bg-white/[0.07]' : 'text-white/35 hover:text-white/70 hover:bg-white/[0.03]'
      }`}>
    {active && (
      <motion.span layoutId="nav-accent"
        className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[2px] rounded-full bg-white"
        transition={{ type: 'spring', stiffness: 500, damping: 35 }} />
    )}
    {tab.label}
  </button>
);

// Gruplanmış menü listesi (hem masaüstü hem mobil)
const NavGroups = ({ activeTab, onSelect }) => (
  <div className="space-y-5">
    {TAB_GROUPS.map(group => (
      <div key={group}>
        <p className="px-4 mb-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-white/20">{group}</p>
        <div className="space-y-0.5">
          {TABS.filter(t => t.group === group).map(tab => (
            <NavItem key={tab.id} tab={tab} active={activeTab === tab.id} onClick={() => onSelect(tab.id)} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Dashboard = () => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('projects');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(() => localStorage.getItem('admin_preview') === '1');
  const handleTab = (id) => { setActiveTab(id); setMobileOpen(false); };
  const togglePreview = () => setPreviewOpen(p => { localStorage.setItem('admin_preview', p ? '0' : '1'); return !p; });
  const ActiveComponent = TAB_COMPONENTS[activeTab];
  const activeMeta = TABS.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-[#050505]" style={{ cursor: 'default' }}>

      {/* Mobil Header */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#050505]/90 backdrop-blur-md">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/30">admin</p>
          <p className="font-bold tracking-tight text-sm">KNOXOPIA</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[9px] uppercase text-white/40">{activeMeta?.label}</span>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="text-white/50 hover:text-white p-1 rounded-md hover:bg-white/5 transition-colors">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobil Menü */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="lg:hidden fixed inset-0 z-50 bg-[#050505] flex flex-col pt-16 px-4 pb-6 overflow-y-auto"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-white/40 hover:text-white p-1 rounded-md hover:bg-white/5 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="mt-4">
              <NavGroups activeTab={activeTab} onSelect={handleTab} />
            </div>
            <button onClick={logout} className="w-full text-left px-4 py-3 mt-6 font-mono text-xs uppercase tracking-[0.15em] text-white/25 hover:text-white/50 border-t border-white/5 transition-colors">
              Çıkış Yap
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex">
        {/* Masaüstü Sidebar */}
        <aside className="hidden lg:flex w-60 flex-shrink-0 border-r border-white/5 flex-col sticky top-0 h-screen">
          <div className="p-6 border-b border-white/5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">admin</p>
            <p className="font-bold tracking-tight">KNOXOPIA</p>
          </div>
          <nav className="flex-1 p-4 overflow-y-auto">
            <NavGroups activeTab={activeTab} onSelect={setActiveTab} />
          </nav>
          <div className="p-4 border-t border-white/5">
            <button onClick={logout} className="w-full text-left px-4 py-2 rounded-md font-mono text-xs uppercase tracking-[0.15em] text-white/25 hover:text-white/60 hover:bg-white/[0.03] transition-all duration-200">
              Çıkış Yap
            </button>
          </div>
        </aside>

        {/* İçerik */}
        <main className="flex-1 overflow-auto min-w-0">
          <div className={`${previewOpen ? 'max-w-none' : 'max-w-5xl'} mx-auto p-4 lg:p-8`}>
            {/* İçerik başlığı */}
            <div className="hidden lg:flex items-baseline gap-3 mb-8 pb-4 border-b border-white/5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/25">{activeMeta?.group}</span>
              <span className="text-white/15">/</span>
              <h1 className="font-mono text-sm uppercase tracking-[0.2em] text-white/80 flex-1">{activeMeta?.label}</h1>
              <button onClick={togglePreview}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-md border transition-all duration-200 active:scale-[0.96] ${previewOpen
                  ? 'border-green-400/50 text-green-400 bg-green-400/10 shadow-[0_0_14px_rgba(74,222,128,0.15)]'
                  : 'border-white/20 text-white/60 hover:border-white/50 hover:text-white'}`}>
                👁 Önizleme {previewOpen ? 'Açık' : ''}
              </button>
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}>
                <ActiveComponent />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        {/* Canlı Önizleme — masaüstünde sağ panel */}
        <AnimatePresence>
          {previewOpen && (
            <motion.aside
              className="hidden lg:block flex-shrink-0 sticky top-0 h-screen"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 480, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <div className="w-[480px] h-full">
                <PreviewPanel onClose={togglePreview} />
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Export ───────────────────────────────────────────────────────
export const AdminPanel = () => (
  <AuthProvider><AdminRouter /></AuthProvider>
);

const AdminRouter = () => {
  const { isAuth } = useAuth();
  return isAuth ? <Dashboard /> : <LoginPage />;
};